// ===================================================================
// 👤 ÁREA DO CANDIDATO - IMPLEMENTAÇÃO COMPLETA
// ===================================================================

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import {
  formatDate,
  formatCurrency,
  formatCourseDisplayFromObject,
  formatCourseDisplayLabel,
  parseDateAsLocal,
  getProcessRegistrationStatus,
} from '../../utils/helpers';
import {
  CANDIDATE_DOC_ACCEPT,
  CANDIDATE_DOC_FORMATS_LABEL,
  candidateDocumentRejectReason,
  isAllowedCandidateDocumentFile,
} from '../../utils/candidateDocumentUpload';
import { getAppealTypeLabel } from '../../constants/displayLabels';
import { downloadBlobFile } from '../../utils/helpers';
import { isOrdemInscricaoMode, isIntentionLetterMode, isCurricularLikeMode } from '../../utils/selectionMode';
import {
  IntentionLetterCandidateField,
  IntentionLetterReadOnly,
  validateIntentionLetterForCourse,
} from '../../components/intentionLetter/IntentionLetterFields';
import { INGRESSA_STORAGE_KEYS } from '../../utils/ingressaBrand';
import { useNotifications } from '../../contexts/NotificationContext';
import { ACTION_ICONS } from '../../constants/statusTranslations';
import { CandidatePwaInstallBanner } from '../../components/pwa/CandidatePwaInstallBanner';
import { CandidatePwaUpdateBanner } from '../../components/pwa/CandidatePwaUpdateBanner';
import { CandidatePushOptInBanner } from '../../components/pwa/CandidatePushOptInBanner';
import { CandidateDocumentFilePicker } from '../../components/candidate/CandidateDocumentFilePicker';
import { syncCandidatePushSubscription, isCandidatePushFeatureActive } from '../../pwa/candidatePush';
import { isCandidatePwaContext } from '../../pwa/candidatePwa';
import { friendlyNetworkError } from '../../utils/friendlyErrors';

const formatCourseChangeHistoryDescription = (change, coursesList) => {
  const fromId = Number(change.from_course_id ?? change.old_course_id);
  const toId = Number(change.to_course_id ?? change.new_course_id);
  const fromCourse = coursesList.find((c) => Number(c.id) === fromId);
  const toCourse = coursesList.find((c) => Number(c.id) === toId);
  const fromLabel = formatCourseDisplayFromObject(
    fromCourse || {
      course_name: change.old_course_name,
      course_campus: change.old_course_campus,
    },
  );
  const toLabel = formatCourseDisplayFromObject(
    toCourse || {
      course_name: change.new_course_name,
      course_campus: change.new_course_campus,
    },
  );
  return `Alteração de curso de ${fromLabel} para ${toLabel}`;
};

const INITIAL_APPLICATION_FORM = {
  course_id: '',
  exam_preferred_venue_id: '',
  quota_type: '',
  curricularDocuments: [],
  requestExemption: false,
  exemptionDocuments: [],
  exemptionNis: '',
  exemptionReason: '',
  isCourseChange: false,
  originalApplicationId: null,
  questionnaireAnswers: {},
  requestSpecialAttendance: false,
  specialAttendanceTypeIds: [],
  specialAttendanceJustification: '',
  specialAttendanceDocuments: [],
  intentionLetterText: '',
};

/** Perguntas do questionário socioeconômico ainda sem alternativa escolhida (chave em answers pode ser id ou string). */
function getUnansweredQuestionnaireQuestions(questionnaire, answers) {
  if (!questionnaire?.questions?.length) return [];
  const ans = answers || {};
  return questionnaire.questions.filter((q) => {
    const v = ans[String(q.id)] ?? ans[q.id];
    return v === undefined || v === null || String(v).trim() === '';
  });
}

/** Total de questões objetivas do processo (soma de question_count nos pesos por área). */
function totalExamQuestionsForProcess(process) {
  const weights = process?.exam_area_weights;
  if (!Array.isArray(weights)) return 0;
  return weights.reduce((sum, row) => {
    const n = parseInt(row?.question_count ?? row?.questions, 10);
    return sum + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
}

function getAnswerKeyAppealsList(application) {
  if (Array.isArray(application?.answer_key_appeals) && application.answer_key_appeals.length > 0) {
    return application.answer_key_appeals;
  }
  return application?.answer_key_appeal ? [application.answer_key_appeal] : [];
}

function getAnswerKeyAppealedQuestionNumbers(application) {
  return new Set(
    getAnswerKeyAppealsList(application)
      .map((a) => Number(a?.question_number))
      .filter((n) => Number.isFinite(n) && n >= 1)
  );
}

function canSubmitMoreAnswerKeyAppeals(application, process) {
  const total = totalExamQuestionsForProcess(process);
  if (total <= 0) return false;
  return getAnswerKeyAppealedQuestionNumbers(application).size < total;
}

function buildAppealsListForView(app) {
  if (!app) return [];
  return [
    app.appeal && { ...app.appeal, appeal_type: app.appeal.appeal_type || 'exemption' },
    app.list_appeal && { ...app.list_appeal, appeal_type: 'list' },
    app.result_appeal && { ...app.result_appeal, appeal_type: 'preliminary_result' },
    app.special_attendance_appeal && { ...app.special_attendance_appeal, appeal_type: 'special_attendance' },
    ...getAnswerKeyAppealsList(app).map((a) => ({ ...a, appeal_type: 'answer_key' })),
  ].filter(Boolean);
}

/** Cronograma já carregado: encerrado quando não há etapa em aberto nem etapa ainda por começar. */
function isProcessScheduleFullyClosed(scheduleItems) {
  const items = Array.isArray(scheduleItems) ? scheduleItems : [];
  if (items.length === 0) return false;
  return !items.some((i) => i.status === 'aberto' || i.status === 'nao_iniciado');
}

export const CandidateArea = React.memo(({ user, onUserUpdate, showProfileModal, onCloseProfileModal }) => {
  // console.log('🔍 CandidateArea renderizando com user:', user);
  
  const [applications, setApplications] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [quotas, setQuotas] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [receiptDownloadingId, setReceiptDownloadingId] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [formData, setFormData] = useState(() => ({ ...INITIAL_APPLICATION_FORM }));
  const [processQuestionnaire, setProcessQuestionnaire] = useState(null);
  const [examVenueOptions, setExamVenueOptions] = useState([]);
  const [error, setError] = useState('');
  const formTopRef = useRef(null);
  const { addNotification } = useNotifications();

  // Estado para histórico de alterações de curso, cota e status
  const [courseChanges, setCourseChanges] = useState([]);
  const [quotaChanges, setQuotaChanges] = useState([]);
  const [statusChanges, setStatusChanges] = useState([]);
  const [matrixCheckResult, setMatrixCheckResult] = useState(null); // { same_matrix, message } quando Trocar Curso

  // Modal Alterar cota
  const [showQuotaChangeModal, setShowQuotaChangeModal] = useState(false);
  const [selectedApplicationForQuotaChange, setSelectedApplicationForQuotaChange] = useState(null);
  const [quotaChangeSelectedQuota, setQuotaChangeSelectedQuota] = useState('');

  // Função para registrar mudanças de status
  const addStatusChange = useCallback(async (applicationId, oldStatus, newStatus, changeType, details = '', processId = 0) => {
    const statusChange = {
      id: Date.now(),
      application_id: applicationId,
      user_id: user.id,
      old_status: oldStatus,
      new_status: newStatus,
      change_type: changeType,
      details: details,
      changed_at: new Date().toISOString()
    };

    try {
      await apiClient.createStatusChange({
        application_id: applicationId,
        process_id: processId,
        description: `${changeType}: ${oldStatus} → ${newStatus}${details ? ' - ' + details : ''}`
      });
      
      // Atualizar estado local
      setStatusChanges(prev => [...prev, statusChange]);
      
      console.log('📊 Status change registrado:', statusChange);
    } catch (error) {
      console.error('❌ Erro ao registrar mudança de status:', error);
    }
  }, [user.id]);
  
  // Estados para modal de upload de documentos (curricular)
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [selectedApplicationForUpload, setSelectedApplicationForUpload] = useState(null);
  const [newDocuments, setNewDocuments] = useState([]);
  const [documentUploadError, setDocumentUploadError] = useState('');
  const [documentFileUploading, setDocumentFileUploading] = useState(false);
  // Estados para modal de reenvio de pedido de isenção
  const [showResendExemptionModal, setShowResendExemptionModal] = useState(false);
  const [selectedApplicationForResendExemption, setSelectedApplicationForResendExemption] = useState(null);
  const [resendExemptionDocs, setResendExemptionDocs] = useState([]);
  const [resendExemptionNis, setResendExemptionNis] = useState('');
  const [resendExemptionError, setResendExemptionError] = useState('');
  const [showResendSpecialAttendanceModal, setShowResendSpecialAttendanceModal] = useState(false);
  const [selectedApplicationForResendSpecialAttendance, setSelectedApplicationForResendSpecialAttendance] = useState(null);
  const [resendSpecialAttendanceDocs, setResendSpecialAttendanceDocs] = useState([]);
  const [resendSpecialAttendanceError, setResendSpecialAttendanceError] = useState('');
  const [rejectionReasons, setRejectionReasons] = useState([]);
  
  // Estados para modal de recurso
  const [showAppealModal, setShowAppealModal] = useState(false);
  const [selectedApplicationForAppeal, setSelectedApplicationForAppeal] = useState(null);
  const [showAppealViewModal, setShowAppealViewModal] = useState(false);
  const [selectedApplicationForAppealView, setSelectedApplicationForAppealView] = useState(null);
  const [appealViewTabIndex, setAppealViewTabIndex] = useState(0);
  const [appealForm, setAppealForm] = useState({
    reason: '',
    documents: [],
    justification: '',
    question_number: ''
  });
  const [appealFormErrors, setAppealFormErrors] = useState({});
  const [appealSubmitError, setAppealSubmitError] = useState('');
  const [answerKeyQuestionHint, setAnswerKeyQuestionHint] = useState(null);
  const [answerKeyHintLoading, setAnswerKeyHintLoading] = useState(false);
  const [answerKeyHintError, setAnswerKeyHintError] = useState('');
  const [appealType, setAppealType] = useState('exemption'); // 'exemption' | 'list' | 'preliminary_result' | 'special_attendance'

  const [specialAttendanceTypesForModal, setSpecialAttendanceTypesForModal] = useState([]);

  // Estado para recolher/expandir processos em Minhas Inscrições (Set de process_id recolhidos)
  // Persiste em localStorage para manter entre logins
  const STORAGE_KEY = INGRESSA_STORAGE_KEYS.candidateCollapsedProcesses;
  const loadCollapsedFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const arr = JSON.parse(stored);
        return new Set(Array.isArray(arr) ? arr.map(Number) : []);
      }
    } catch (_) { /* ignore */ }
    return new Set();
  };
  const [collapsedProcessIds, setCollapsedProcessIds] = useState(loadCollapsedFromStorage);

  // Modal Editais e anexos do processo
  const [docsModalProcess, setDocsModalProcess] = useState(null);
  const [processDocuments, setProcessDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Modal Cronograma do processo
  const [scheduleModalProcess, setScheduleModalProcess] = useState(null);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);


  // Mapa de cronogramas por processo (para aviso de etapa em aberto no card)
  const [processScheduleMap, setProcessScheduleMap] = useState({});
  /** Último schedule_updated_at reconhecido por processo (chave string id) */
  const [scheduleAckMap, setScheduleAckMap] = useState({});
  const [scheduleAckBusy, setScheduleAckBusy] = useState(false);

  // Modal Meus dados cadastrais (aberto via Header ou internamente)
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileFormData, setProfileFormData] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');

  const formatCPF = useCallback((v) => {
    const n = (v || '').replace(/[^\d]/g, '');
    return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }, []);
  const formatPhone = useCallback((v) => {
    const n = (v || '').replace(/[^\d]/g, '');
    return n.length <= 10 ? n.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3') : n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }, []);

  const handleOpenProfileModal = useCallback(async () => {
    try {
      const { data } = await apiClient.auth.getSession();
      const u = data?.session?.user || user;
      setProfileFormData({
        name: u.name || '',
        mother_name: u.mother_name || '',
        father_name: u.father_name || '',
        birth_date: u.birth_date || '',
        cpf: u.cpf || '',
        identity_type: u.identity_type || 'CI',
        identity_number: u.identity_number || '',
        issuing_org: u.issuing_org || '',
        issuing_date: u.issuing_date || '',
        phone: u.phone || '',
        social_name: u.social_name || '',
        use_social_name: !!u.use_social_name,
        address: u.address || { street: '', number: '', complement: '', neighborhood: '', city: '', state: '', zipCode: '' }
      });
      setProfileError('');
      setProfileModalOpen(true);
    } catch {
      setProfileError('Erro ao carregar dados');
    }
  }, [user]);

  const handleProfileInputChange = useCallback((field, value) => {
    setProfileFormData(prev => {
      if (!prev) return prev;
      if (field.includes('.')) {
        const [p, c] = field.split('.');
        return { ...prev, [p]: { ...prev[p], [c]: value } };
      }
      return { ...prev, [field]: value };
    });
    setProfileError('');
  }, []);

  const validateCPF = useCallback((cpf) => {
    const digits = (cpf || '').replace(/[^\d]/g, '');
    if (digits.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(digits)) return false;
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(digits.charAt(i)) * (10 - i);
    let r = (sum * 10) % 11;
    if (r === 10) r = 0;
    if (r !== parseInt(digits.charAt(9))) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(digits.charAt(i)) * (11 - i);
    r = (sum * 10) % 11;
    if (r === 10) r = 0;
    return r === parseInt(digits.charAt(10));
  }, []);

  const validateProfileForm = useCallback(() => {
    if (!profileFormData) return false;
    if (!profileFormData.name?.trim()) { setProfileError('Nome é obrigatório'); return false; }
    if (!profileFormData.cpf?.trim()) { setProfileError('CPF é obrigatório'); return false; }
    if (!validateCPF(profileFormData.cpf)) { setProfileError('CPF inválido'); return false; }
    if (!profileFormData.birth_date?.trim()) { setProfileError('Data de nascimento é obrigatória'); return false; }
    if (!profileFormData.mother_name?.trim()) { setProfileError('Nome da mãe é obrigatório'); return false; }
    if (!profileFormData.identity_number?.trim()) { setProfileError('Número do documento (CI ou CNH) é obrigatório'); return false; }
    if (!profileFormData.issuing_org?.trim()) { setProfileError('Órgão emissor é obrigatório'); return false; }
    if (!profileFormData.issuing_date?.trim()) { setProfileError('Data de emissão é obrigatória'); return false; }
    if (!profileFormData.phone?.trim()) { setProfileError('Telefone é obrigatório'); return false; }
    if (!profileFormData.address?.street?.trim()) { setProfileError('Endereço é obrigatório'); return false; }
    if (!profileFormData.address?.city?.trim()) { setProfileError('Cidade é obrigatória'); return false; }
    if (!profileFormData.address?.state?.trim()) { setProfileError('Estado é obrigatório'); return false; }
    return true;
  }, [profileFormData, validateCPF]);

  const handleProfileSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!profileFormData) return;
    if (!validateProfileForm()) return;
    setProfileSaving(true);
    setProfileError('');
    try {
      const { data, error: err } = await apiClient.auth.updateMe({
        name: profileFormData.name,
        mother_name: profileFormData.mother_name,
        father_name: profileFormData.father_name,
        birth_date: profileFormData.birth_date,
        cpf: profileFormData.cpf?.replace(/[^\d]/g, ''),
        identity_type: profileFormData.identity_type,
        identity_number: profileFormData.identity_number,
        issuing_org: profileFormData.issuing_org,
        issuing_date: profileFormData.issuing_date,
        phone: profileFormData.phone,
        social_name: profileFormData.social_name,
        use_social_name: profileFormData.use_social_name,
        address: profileFormData.address
      });
      if (err) {
        setProfileError(err);
      } else if (data?.user && onUserUpdate) {
        onUserUpdate(data.user);
        addNotification('Sucesso', 'Dados atualizados com sucesso', 'success');
        setProfileModalOpen(false);
        onCloseProfileModal?.();
      }
    } catch {
      setProfileError('Erro ao salvar');
    } finally {
      setProfileSaving(false);
    }
  }, [profileFormData, validateProfileForm, onUserUpdate, addNotification]);

  const toggleProcessExpand = useCallback((processId) => {
    setCollapsedProcessIds(prev => {
      const next = new Set(prev);
      if (next.has(processId)) next.delete(processId);
      else next.add(processId);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  // Função para remover aplicações duplicadas
  const removeDuplicateApplications = useCallback((applications) => {
    console.log('🧹 Verificando duplicatas em aplicações...');
    
    // Agrupar por user_id, process_id e course_id
    const grouped = {};
    applications.forEach(app => {
      const key = `${app.user_id}-${app.process_id}-${app.course_id}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(app);
    });
    
    // Para cada grupo, manter apenas a aplicação mais recente
    const cleanedApplications = [];
    Object.keys(grouped).forEach(key => {
      const apps = grouped[key];
      if (apps.length > 1) {
        console.log(`🔄 Encontradas ${apps.length} aplicações duplicadas para chave ${key}`);
        
        // Ordenar por data de criação (mais recente primeiro)
        apps.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        // Manter apenas a primeira (mais recente)
        cleanedApplications.push(apps[0]);
        
        console.log('✅ Mantendo aplicação ID:', apps[0].id, 'criada em:', apps[0].created_at);
        console.log('🗑️ Removendo duplicatas:', apps.slice(1).map(a => `ID ${a.id} (${a.created_at})`));
      } else {
        cleanedApplications.push(apps[0]);
      }
    });
    
    console.log(`📊 Aplicações antes: ${applications.length}, depois: ${cleanedApplications.length}`);
    return cleanedApplications;
  }, []);

  const loadData = useCallback(async (isBackground = false) => {
    if (!user) return;
    if (!isBackground) setLoading(true);
    try {
      const [processesData, coursesData, quotasData, paymentsData, applicationsData, courseChangesData, quotaChangesData, statusChangesData, rejectionReasonsData, scheduleAcksData] = await Promise.all([
        /* Incluir inativos: título em Minhas Inscrições e dados da inscrição usam o mesmo objeto processo */
        apiClient.getProcesses(true),
        apiClient.getCourses(),
        apiClient.getQuotas(),
        apiClient.getPayments(),
        apiClient.getApplications(),
        apiClient.getCourseChanges(),
        apiClient.getQuotaChanges(),
        apiClient.getStatusChanges(),
        apiClient.getRejectionReasons(),
        apiClient.getMyScheduleAcks().catch(() => ({ acks: {} })),
      ]);
      
      setProcesses(Array.isArray(processesData) ? processesData : []);
      setScheduleAckMap((scheduleAcksData && scheduleAcksData.acks) ? scheduleAcksData.acks : {});
      setCourses(Array.isArray(coursesData) ? coursesData : []);
      setQuotas(Array.isArray(quotasData) ? quotasData : []);
      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      const apps = Array.isArray(applicationsData) ? applicationsData : (applicationsData?.data || []);
      const cleanedApplications = removeDuplicateApplications(apps);
      setApplications(cleanedApplications);
      setCourseChanges(Array.isArray(courseChangesData) ? courseChangesData : []);
      setQuotaChanges(Array.isArray(quotaChangesData) ? quotaChangesData : []);
      setStatusChanges(Array.isArray(statusChangesData) ? statusChangesData : []);
      setRejectionReasons(Array.isArray(rejectionReasonsData) ? rejectionReasonsData : []);
      
      console.log('📊 Dados carregados:', {
        processes: processesData.data?.length || 0,
        courses: coursesData.data?.length || 0,
        payments: Array.isArray(paymentsData) ? paymentsData.length : 0,
        applications: applicationsData.data?.length || 0,
        courseChanges: courseChangesData.data?.length || 0
      });
      
      // 🔍 DEBUG: Verificar courseChanges carregados
      if (courseChangesData.data && courseChangesData.data.length > 0) {
        console.log('🔍 DEBUG - courseChanges carregados:', courseChangesData.data);
        courseChangesData.data.forEach((change, index) => {
          console.log(`🔍 DEBUG - courseChange ${index + 1}:`, {
            id: change.id,
            old_course_name: change.old_course_name,
            new_course_name: change.new_course_name,
            old_course_id: change.old_course_id,
            new_course_id: change.new_course_id,
            changed_at: change.changed_at
          });
        });
      }
      
      // 🔍 DEBUG: Verificar tipos de dados
      if (applicationsData.data?.length > 0) {
        console.log('🔍 Primeira aplicação:', applicationsData.data[0]);
        console.log('🔍 Tipo do course_id:', typeof applicationsData.data[0].course_id);
      }
      if (coursesData.data?.length > 0) {
        console.log('🔍 Primeiro curso:', coursesData.data[0]);
        console.log('🔍 Tipo do id do curso:', typeof coursesData.data[0].id);
      }
      
      // 🔍 DEBUG: Verificar se o processo "Teste persistente" está nos dados
      const testProcess = processesData.data?.find(p => p.name === 'Teste persistente');
      console.log('🔍 Processo "Teste persistente" encontrado:', testProcess);
      console.log('🔍 Todos os processos:', processesData.data?.map(p => ({ id: p.id, name: p.name, is_active: p.is_active })));
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      if (!isBackground) setError('Erro ao carregar dados');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [user]);

  const acknowledgeScheduleNotice = useCallback(async (processIds) => {
    if (!processIds?.length) return;
    setScheduleAckBusy(true);
    const applyLocalAck = () => {
      setScheduleAckMap((prev) => {
        const next = { ...prev };
        processIds.forEach((id) => {
          const proc = processes.find((p) => String(p.id) === String(id));
          if (proc?.schedule_updated_at) next[String(id)] = proc.schedule_updated_at;
        });
        return next;
      });
    };
    try {
      const res = await apiClient.acknowledgeScheduleChanges(processIds);
      if (res?.acks) {
        setScheduleAckMap(res.acks);
      } else {
        applyLocalAck();
      }
    } catch (err) {
      applyLocalAck();
      addNotification('Erro', err.message || 'Não foi possível registrar a leitura.', 'error');
    } finally {
      setScheduleAckBusy(false);
    }
  }, [addNotification, processes]);

  const hasModalOpen = showApplicationModal || showDocumentUploadModal || showResendExemptionModal || showResendSpecialAttendanceModal || showAppealModal || showAppealViewModal || showQuotaChangeModal;

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  useEffect(() => {
    if (!user || !isCandidatePushFeatureActive() || !isCandidatePwaContext()) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      syncCandidatePushSubscription().catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (showProfileModal && !profileFormData) {
      handleOpenProfileModal();
    }
  }, [showProfileModal, profileFormData, handleOpenProfileModal]);

  useEffect(() => {
    if (!user) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !hasModalOpen) loadData(true);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [user, loadData, hasModalOpen]);

  useEffect(() => {
    if (!user || hasModalOpen) return;
    const interval = setInterval(() => loadData(true), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, loadData, hasModalOpen]);

  // Carregar questionário socioeconômico quando abrir modal de nova inscrição (não troca de curso)
  useEffect(() => {
    if (!showApplicationModal || !selectedProcess || formData.isCourseChange) {
      if (!showApplicationModal) setProcessQuestionnaire(null);
      return;
    }
    const qid = selectedProcess.questionnaire_id;
    if (!qid) {
      setProcessQuestionnaire(null);
      return;
    }
    apiClient.getProcessQuestionnaire(selectedProcess.id)
      .then(res => {
        const q = res?.questionnaire;
        setProcessQuestionnaire(q || null);
      })
      .catch(() => setProcessQuestionnaire(null));
  }, [showApplicationModal, selectedProcess?.id, selectedProcess?.questionnaire_id, formData.isCourseChange]);

  useEffect(() => {
    if (!showApplicationModal || !selectedProcess || formData.isCourseChange) {
      if (!showApplicationModal) setExamVenueOptions([]);
      return;
    }
    if (selectedProcess.selection_mode !== 'prova' || selectedProcess.exam_allocation_policy !== 'candidate_choice') {
      setExamVenueOptions([]);
      return;
    }
    apiClient.getExamVenues(selectedProcess.id)
      .then((res) => setExamVenueOptions(Array.isArray(res) ? res : []))
      .catch(() => setExamVenueOptions([]));
  }, [showApplicationModal, selectedProcess?.id, selectedProcess?.selection_mode, selectedProcess?.exam_allocation_policy, formData.isCourseChange]);

  useEffect(() => {
    if (!showApplicationModal || !selectedProcess || formData.isCourseChange) {
      setSpecialAttendanceTypesForModal([]);
      return;
    }
    if (selectedProcess.selection_mode !== 'prova') {
      setSpecialAttendanceTypesForModal([]);
      return;
    }
    if (!selectedProcess.use_special_attendance_specialized && !selectedProcess.use_special_attendance_specific) {
      setSpecialAttendanceTypesForModal([]);
      return;
    }
    let cancelled = false;
    apiClient.getSpecialAttendanceTypes({ processId: selectedProcess.id })
      .then((data) => {
        if (!cancelled) setSpecialAttendanceTypesForModal(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setSpecialAttendanceTypesForModal([]);
      });
    return () => { cancelled = true; };
  }, [showApplicationModal, selectedProcess?.id, selectedProcess?.selection_mode, selectedProcess?.use_special_attendance_specialized, selectedProcess?.use_special_attendance_specific, formData.isCourseChange]);

  const loadProcessDocuments = useCallback(async (processId) => {
    if (!processId) return;
    try {
      setLoadingDocs(true);
      const data = await apiClient.getProcessDocuments(processId);
      setProcessDocuments(Array.isArray(data) ? data : []);
    } catch {
      setProcessDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  useEffect(() => {
    if (docsModalProcess?.id) loadProcessDocuments(docsModalProcess.id);
  }, [docsModalProcess?.id, loadProcessDocuments]);

  const loadProcessSchedule = useCallback(async (processId) => {
    if (!processId) return;
    try {
      setLoadingSchedule(true);
      const data = await apiClient.getProcessSchedule(processId);
      setScheduleItems(Array.isArray(data) ? data : []);
    } catch {
      setScheduleItems([]);
    } finally {
      setLoadingSchedule(false);
    }
  }, []);

  useEffect(() => {
    if (scheduleModalProcess?.id) loadProcessSchedule(scheduleModalProcess.id);
  }, [scheduleModalProcess?.id, loadProcessSchedule]);

  // Carregar cronogramas dos processos exibidos (para aviso de etapa em aberto)
  useEffect(() => {
    const ids = new Set([
      ...processes.map(p => p.id),
      ...applications.map(a => a.process_id)
    ].filter(Boolean));
    if (ids.size === 0) return;
    const fetchAll = async () => {
      const map = {};
      await Promise.all([...ids].map(async (pid) => {
        try {
          const data = await apiClient.getProcessSchedule(pid);
          map[pid] = Array.isArray(data) ? data : [];
        } catch {
          map[pid] = [];
        }
      }));
      setProcessScheduleMap(prev => ({ ...prev, ...map }));
    };
    fetchAll();
  }, [processes, applications]);

  // Verificar matriz ao selecionar curso no Trocar Curso
  useEffect(() => {
    if (!formData.isCourseChange || !formData.originalApplicationId || !formData.course_id) {
      setMatrixCheckResult(null);
      return;
    }
    const orig = applications.find(a => a.id === formData.originalApplicationId);
    if (!orig || !courses.length) return;
    const oldId = Number(orig.course_id);
    const newId = Number(formData.course_id);
    const process = processes.find(p => p.id === orig.process_id);
    if (process && isOrdemInscricaoMode(process.selection_mode)) {
      setMatrixCheckResult({
        same_matrix: true,
        message: oldId === newId ? 'Mesmo curso' : 'Processo por ordem de inscrição — apenas troca de curso/unidade',
      });
      return;
    }
    if (oldId === newId) {
      setMatrixCheckResult({ same_matrix: true, message: 'Mesmo curso' });
      return;
    }
    apiClient.compareCourseMatrices(oldId, newId)
      .then(r => setMatrixCheckResult(r))
      .catch(() => setMatrixCheckResult(null));
  }, [formData.isCourseChange, formData.originalApplicationId, formData.course_id, applications, courses, processes]);

  // Funções auxiliares
  const getPaymentStatus = useCallback((processId) => {
    const application = applications.find(app => app.process_id === processId);
    if (!application) return 'pending';
    if (application.exemption_status === 'approved' || application.exemption_status === 'appeal_approved') {
      return 'exempt';
    }
    return application.payment_status || 'pending';
  }, [applications]);

  const getPaymentAmount = useCallback((processId) => {
    // Buscar processo para obter o valor da taxa
    const process = processes.find(p => p.id === processId);
    
    if (!process) return 0;
    
    return process.fee_amount || 0;
  }, [processes]);

  /**
   * Gera os selos de status para uma aplicação
   * 
   * IMPORTANTE: Esta função gera APENAS 2 selos:
   * 1. paymentStep - Status de pagamento/isenção
   * 2. analysisStep - Status da análise curricular
   * 
   * NÃO adicionar mais selos aqui para evitar duplicação!
   * 
   * @param {Object} application - Aplicação do candidato
   * @returns {Object} Objeto com paymentStep, analysisStep e overallStatus
   */
  const getApplicationStatus = useCallback((application) => {
    const process = processes.find(p => p.id === application.process_id);
    const paymentStatus = getPaymentStatus(process?.id);
    
    if (application.status === 'rejected') return 'rejected';
    if (application.status === 'approved') return 'approved';
    if (application.status === 'pending_exemption') return 'pending_exemption';
    if (application.status === 'pending') {
      if (!process?.has_fee) return 'pending';
      // Se o pagamento foi confirmado, mostrar como "Pagamento Confirmado"
      if (paymentStatus === 'paid' || paymentStatus === 'exempt') {
        return 'payment_confirmed';
      }
      return 'pending';
    }
    return 'unknown';
  }, [processes, getPaymentStatus]);

  // Função para verificar se está no período de isenção (só exibe opção quando start <= hoje <= end)
  const isInExemptionPeriod = useCallback((process) => {
    if (!process?.exemption_start || !process?.exemption_end) return false;
    
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr).trim();
      if (s.includes('-')) {
        const parts = s.split('-');
        if (parts.length >= 3) {
          const y = parseInt(parts[0], 10);
          const m = parseInt(parts[1], 10) - 1;
          const d = parseInt(parts[2], 10);
          return new Date(y, m, d);
        }
      }
      if (s.includes('/')) {
        const parts = s.split('/');
        if (parts.length >= 3) {
          const d = parseInt(parts[0], 10);
          const m = parseInt(parts[1], 10) - 1;
          const y = parseInt(parts[2], 10);
          return new Date(y, m, d);
        }
      }
      return null;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = parseDate(process.exemption_start);
    const end = parseDate(process.exemption_end);
    if (!start || !end) return false;
    
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return today >= start && today <= end;
  }, []);

  const processOffersSpecialAttendance = useCallback((process) => {
    if (!process || process.selection_mode !== 'prova') return false;
    return !!(process.use_special_attendance_specialized || process.use_special_attendance_specific);
  }, []);

  const isInSpecialAttendanceRequestPeriod = useCallback((process) => {
    if (!process?.special_attendance_start || !process?.special_attendance_end) return false;
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr).trim();
      if (s.includes('-')) {
        const parts = s.split('-');
        if (parts.length >= 3) {
          const y = parseInt(parts[0], 10);
          const m = parseInt(parts[1], 10) - 1;
          const d = parseInt(parts[2], 10);
          return new Date(y, m, d);
        }
      }
      if (s.includes('/')) {
        const parts = s.split('/');
        if (parts.length >= 3) {
          const d = parseInt(parts[0], 10);
          const m = parseInt(parts[1], 10) - 1;
          const y = parseInt(parts[2], 10);
          return new Date(y, m, d);
        }
      }
      return null;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = parseDate(process.special_attendance_start);
    const end = parseDate(process.special_attendance_end);
    if (!start || !end) return false;
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return today >= start && today <= end;
  }, []);

  /** Recurso contra indeferimento de atendimento especial (datas dedicadas no processo). */
  const isInSpecialAttendanceAppealPeriod = useCallback((process) => {
    if (!process?.special_attendance_appeal_start || !process?.special_attendance_appeal_end) return false;
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr).trim();
      if (s.includes('-')) {
        const parts = s.split('-');
        if (parts.length >= 3) {
          const y = parseInt(parts[0], 10);
          const m = parseInt(parts[1], 10) - 1;
          const d = parseInt(parts[2], 10);
          return new Date(y, m, d);
        }
      }
      if (s.includes('/')) {
        const parts = s.split('/');
        if (parts.length >= 3) {
          const d = parseInt(parts[0], 10);
          const m = parseInt(parts[1], 10) - 1;
          const y = parseInt(parts[2], 10);
          return new Date(y, m, d);
        }
      }
      return null;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = parseDate(process.special_attendance_appeal_start);
    const end = parseDate(process.special_attendance_appeal_end);
    if (!start || !end) return false;
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return today >= start && today <= end;
  }, []);

  // Função para verificar se está no período de recurso contra o Resultado preliminar
  const isInResultAppealPeriod = useCallback((process) => {
    if (!process?.result_appeal_start || !process?.result_appeal_end) return false;
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr);
      if (s.includes('-')) {
        const [y, m, d] = s.split('-');
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      }
      const [d, m, y] = s.split('/');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    };
    const now = new Date();
    const start = parseDate(process.result_appeal_start);
    const end = parseDate(process.result_appeal_end);
    if (end) end.setHours(23, 59, 59, 999);
    return start && end && !isNaN(start.getTime()) && !isNaN(end.getTime()) && now >= start && now <= end;
  }, []);

  const isInAnswerKeyAppealPeriod = useCallback((process) => {
    if (!process?.answer_key_appeal_start || !process?.answer_key_appeal_end) return false;
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr);
      if (s.includes('-')) {
        const [y, m, d] = s.split('-');
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      }
      const [d, m, y] = s.split('/');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    };
    const now = new Date();
    const start = parseDate(process.answer_key_appeal_start);
    const end = parseDate(process.answer_key_appeal_end);
    if (end) end.setHours(23, 59, 59, 999);
    return start && end && !isNaN(start.getTime()) && !isNaN(end.getTime()) && now >= start && now <= end;
  }, []);

  const isProvaEnrollmentConfirmed = useCallback((application) => {
    const process = processes.find(p => p.id === application.process_id);
    if (process && !process.has_fee) return true;
    const ps = (application?.payment_status || '').toLowerCase();
    if (['paid', 'exempted'].includes(ps)) return true;
    const es = (application?.exemption_status || '').toLowerCase();
    return ['approved', 'appeal_approved', 'exemption_appeal_approved'].includes(es);
  }, [processes]);

  /** Janela de recurso AT especial: datas dedicadas OU (modo prova com AT) o mesmo prazo de recurso contra resultado preliminar. */
  const isInSpecialAttendanceAppealScheduleWindow = useCallback((process) => {
    if (isInSpecialAttendanceAppealPeriod(process)) return true;
    if (process?.selection_mode !== 'prova') return false;
    if (!processOffersSpecialAttendance(process)) return false;
    return isInResultAppealPeriod(process);
  }, [isInSpecialAttendanceAppealPeriod, processOffersSpecialAttendance, isInResultAppealPeriod]);

  // Função para verificar se está no período de recurso contra a Lista de inscritos
  const isInListAppealPeriod = useCallback((process) => {
    if (!process?.registration_appeal_start || !process?.registration_appeal_end) return false;
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr);
      if (s.includes('-')) {
        const [y, m, d] = s.split('-');
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      }
      const [d, m, y] = s.split('/');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    };
    const now = new Date();
    const start = parseDate(process.registration_appeal_start);
    const end = parseDate(process.registration_appeal_end);
    if (end) end.setHours(23, 59, 59, 999);
    return start && end && !isNaN(start.getTime()) && !isNaN(end.getTime()) && now >= start && now <= end;
  }, []);

  // Função para verificar se está no período de recurso de isenção
  const isInAppealPeriod = useCallback((process) => {
    console.log('🔍 DEBUG - isInAppealPeriod chamada com:', process);
    
    if (!process?.exemption_appeal_start || !process?.exemption_appeal_end) {
      console.log('🔍 DEBUG - Processo sem datas de recurso');
      return false;
    }
    
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr);
      if (s.includes('-')) {
        const [y, m, d] = s.split('-');
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      }
      const [d, m, y] = s.split('/');
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    };
    const now = new Date();
    const start = parseDate(process.exemption_appeal_start);
    const end = parseDate(process.exemption_appeal_end);
    if (end) end.setHours(23, 59, 59, 999); // último dia inclui o dia inteiro
    
    const result = now >= start && now <= end;
    
    console.log('🔍 DEBUG - isInAppealPeriod resultado:', {
      appealStart: process.exemption_appeal_start,
      appealEnd: process.exemption_appeal_end,
      startDate: start,
      endDate: end,
      currentDate: now,
      currentDateFormatted: now.toLocaleDateString('pt-BR'),
      result,
      resultType: typeof result
    });
    
    return result;
  }, []);

  const isInRegistrationPeriod = useCallback((process) => {
    if (!process?.start_date || !process?.end_date) return false;
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const s = String(dateStr);
      if (s.includes('-')) {
        const [y, m, d] = s.split('-');
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      }
      const parts = s.split('/');
      if (parts.length === 3) {
        const [d, m, y] = parts;
        return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      }
      return null;
    };
    const now = new Date();
    const start = parseDate(process.start_date);
    const end = parseDate(process.end_date);
    if (end) end.setHours(23, 59, 59, 999);
    return start && end && !isNaN(start.getTime()) && !isNaN(end.getTime()) && now >= start && now <= end;
  }, []);

  // Função global para testar datas
  window.testDateLogic = (process) => {
    console.log('🧪 TESTANDO LÓGICA DE DATAS:');
    console.log('Processo:', process);
    
    const convertBrazilianDate = (dateStr) => {
      if (!dateStr) return null;
      const [day, month, year] = dateStr.split('/');
      return new Date(year, month - 1, day);
    };
    
    const now = new Date();
    const exemptionStart = convertBrazilianDate(process?.exemption_start);
    const exemptionEnd = convertBrazilianDate(process?.exemption_end);
    const appealStart = convertBrazilianDate(process?.exemption_appeal_start);
    const appealEnd = convertBrazilianDate(process?.exemption_appeal_end);
    
    console.log('📅 DATAS CONVERTIDAS:');
    console.log('  - Data atual:', now.toLocaleDateString('pt-BR'), '(', now, ')');
    console.log('  - Início isenção:', process?.exemption_start, '→', exemptionStart);
    console.log('  - Fim isenção:', process?.exemption_end, '→', exemptionEnd);
    console.log('  - Início recurso:', process?.exemption_appeal_start, '→', appealStart);
    console.log('  - Fim recurso:', process?.exemption_appeal_end, '→', appealEnd);
    
    console.log('🔍 COMPARAÇÕES:');
    console.log('  - Está no período de isenção?', now >= exemptionStart && now <= exemptionEnd);
    console.log('  - Está no período de recurso?', now >= appealStart && now <= appealEnd);
    
    return {
      currentDate: now,
      isInExemptionPeriod: now >= exemptionStart && now <= exemptionEnd,
      isInAppealPeriod: now >= appealStart && now <= appealEnd
    };
  };

  // Função para obter descrição do motivo de rejeição
  const getRejectionReasonDescription = useCallback((rejectionReasonId) => {
    if (!rejectionReasonId) return null;
    const reason = rejectionReasons.find(r => r.id == rejectionReasonId || String(r.id) === String(rejectionReasonId));
    return reason ? reason.title : null;
  }, [rejectionReasons]);

  // Função para obter status detalhado da inscrição
  const getDetailedApplicationStatus = useCallback((application) => {
    const process = processes.find(p => p.id === application.process_id);
    const processHasFee = !!process?.has_fee;
    const paymentStatus = getPaymentStatus(process?.id);
    
    // Status de pagamento/isenção (primeiro selo) — só quando o processo cobra taxa
    let paymentStep = null;
    
    if (processHasFee) {
    paymentStep = {
      status: 'pending',
      label: 'Pagamento Pendente',
      description: 'Aguardando confirmação do pagamento',
      color: '#F59E0B',
      icon: '💰'
    };
    
    // ========================================
    // SELO 1: Isenção (se pediu) OU Pagamento (só quando Gerar GRU)
    // Pagamento confirmado pelo admin tem prioridade (sobrepõe status de isenção)
    // ========================================
    if (paymentStatus === 'paid' || application.payment_status === 'paid') {
      paymentStep = {
        status: 'completed',
        label: 'Pagamento Confirmado',
        description: 'Pagamento recebido e confirmado',
        color: '#10B981',
        icon: '✅'
      };
    } else {
      const isExemptionFlow = application.is_exemption_requested ?? (application.exemption_status && application.exemption_status !== 'not_requested');
      if (isExemptionFlow) {
        // Fluxo de isenção: selos por fase
        if (application.exemption_status === 'pending') {
        paymentStep = {
          status: 'pending',
          label: 'Aguardando Análise da Isenção',
          description: 'Aguardando análise do pedido de isenção',
          color: '#F59E0B',
          icon: '⏳'
        };
      } else if (application.exemption_status === 'approved') {
        paymentStep = {
          status: 'completed',
          label: 'Isenção Deferida',
          description: 'Solicitação de isenção aprovada',
          color: '#10B981',
          icon: '✅'
        };
      } else if (application.exemption_status === 'rejected') {
        const reason = (application.exemption_reason || '').trim();
        let desc = 'Reenvie o pedido ou gere a GRU para pagar';
        if (reason && reason !== '1' && reason !== '1.') {
          desc = reason;
        } else if (/^\d+$/.test(reason)) {
          const resolved = getRejectionReasonDescription(reason);
          if (resolved) desc = resolved;
        }
        paymentStep = {
          status: 'rejected',
          label: 'Pagamento Pendente - Isenção Indeferida',
          description: desc,
          color: '#EF4444',
          icon: '❌'
        };
      } else if (application.exemption_status === 'appealed') {
        paymentStep = {
          status: 'pending',
          label: 'Recurso de Isenção em Análise',
          description: 'Aguardando análise do recurso',
          color: '#8B5CF6',
          icon: '⏳'
        };
      } else if (application.exemption_status === 'appeal_approved') {
        paymentStep = {
          status: 'completed',
          label: 'Recurso de Isenção Aprovado',
          description: 'Recurso contra indeferimento aprovado - isento da taxa',
          color: '#10B981',
          icon: '✅'
        };
      } else if (application.exemption_status === 'appeal_rejected') {
        const reason = (application.exemption_reason || '').trim();
        let desc = 'Reenvie o pedido ou gere a GRU para pagar';
        if (reason && reason !== '1' && reason !== '1.') {
          desc = reason;
        } else if (/^\d+$/.test(reason)) {
          const resolved = getRejectionReasonDescription(reason);
          if (resolved) desc = resolved;
        }
        paymentStep = {
          status: 'rejected',
          label: 'Pagamento Pendente - Recurso Indeferido',
          description: desc,
          color: '#EF4444',
          icon: '❌'
        };
      }
      } else {
        // Não pediu isenção: Pagamento Pendente (Gerar GRU)
        paymentStep = {
          status: 'pending',
          label: 'Pagamento Pendente',
          description: 'Gere a GRU para pagar a taxa de inscrição',
          color: '#F59E0B',
          icon: '💰'
        };
      }
    }
    }
    
    // ========================================
    // Modo prova: até 3 selos (pagamento | atendimento especial se houver pedido | inscrição na prova)
    // Modo análise: selo 2 = análise curricular
    // ========================================
    const isProvaProcess = process?.selection_mode === 'prova';
    const isOrdemProcess = isOrdemInscricaoMode(process?.selection_mode);
    let analysisStep = {
      status: 'pending',
      label: isOrdemProcess ? 'Aguardando Análise de Inscrição' : 'Aguardando Análise Curricular',
      description: isOrdemProcess
        ? 'Inscrição na fila para verificação de documentos'
        : 'Inscrição na fila para análise curricular',
      color: '#6B7280',
      icon: '⏳',
      bg: '#F3F4F6'
    };

    let provaAttendanceStep = null;
    let provaEnrollmentStep = null;

    if (isProvaProcess) {
      const sarId = application.special_attendance_request_id;
      const rawSarStatus = application.special_attendance_request_status;
      const sarStatus = rawSarStatus == null || rawSarStatus === '' ? '' : String(rawSarStatus).toLowerCase();
      const payStepDone = !processHasFee || paymentStep?.status === 'completed';

      if (sarId) {
        if (sarStatus === 'pending' || sarStatus === '' || sarStatus === 'analyzing') {
          provaAttendanceStep = {
            status: 'pending',
            label: 'Atendimento especial',
            description: 'Pedido aguardando análise',
            color: '#6B7280',
            icon: '♿',
            bg: '#F3F4F6'
          };
        } else if (sarStatus === 'approved') {
          provaAttendanceStep = {
            status: 'completed',
            label: 'Atendimento especial',
            description: 'Pedido deferido',
            color: '#10B981',
            icon: '✅',
            bg: '#F0FDF4'
          };
        } else if (sarStatus === 'rejected') {
          const saAppeal = application.special_attendance_appeal;
          const saAppealSt = saAppeal ? String(saAppeal.status || '').toLowerCase() : '';
          let attDesc = 'Pedido indeferido — no período de pedido de atendimento especial você pode reenviar documentos (botão na inscrição)';
          if (saAppealSt === 'rejected') {
            attDesc = 'Recurso contra indeferimento indeferido';
          } else if (saAppealSt === 'approved') {
            attDesc = 'Recurso contra indeferimento deferido';
          } else {
            const rr = (application.special_attendance_rejection_reason || '').toString().trim();
            if (/^\d+$/.test(rr)) {
              const resolved = getRejectionReasonDescription(rr);
              if (resolved) attDesc = resolved;
            }
          }
          provaAttendanceStep = {
            status: 'rejected',
            label: 'Atendimento especial',
            description: attDesc,
            color: '#EF4444',
            icon: '❌',
            bg: '#FEF2F2'
          };
        } else if (sarStatus === 'appealed') {
          provaAttendanceStep = {
            status: 'pending',
            label: 'Atendimento especial',
            description: 'Recurso contra indeferimento em análise',
            color: '#8B5CF6',
            icon: '⏳',
            bg: '#F5F3FF'
          };
        } else if (sarStatus === 'returned') {
          provaAttendanceStep = {
            status: 'pending',
            label: 'Atendimento especial',
            description: 'Pedido devolvido para ajuste — reenvie conforme orientações',
            color: '#F59E0B',
            icon: '↩️',
            bg: '#FFFBEB'
          };
        } else {
          provaAttendanceStep = {
            status: 'pending',
            label: 'Atendimento especial',
            description: 'Acompanhe o status do seu pedido no processo',
            color: '#6B7280',
            icon: '♿',
            bg: '#F3F4F6'
          };
        }
      }

      provaEnrollmentStep = payStepDone
        ? {
            status: 'completed',
            label: 'Processo por prova',
            description: 'Inscrição confirmada. Acompanhe cronograma e editais do certame.',
            color: '#10B981',
            icon: '✅',
            bg: '#F0FDF4'
          }
        : {
            status: 'pending',
            label: 'Processo por prova',
            description: 'Inscrição registrada. Acompanhe cronograma e editais do certame.',
            color: '#6366F1',
            icon: '📝',
            bg: '#EEF2FF'
          };

      analysisStep = null;
    } else if (application.analysis_status === 'approved') {
      const proc = processes.find(p => p.id === application.process_id);
      const ordemProc = isOrdemInscricaoMode(proc?.selection_mode);
      const showAvg = !ordemProc && proc?.show_candidate_average !== false;
      const mediaFinal = showAvg && application.final_score != null && !isNaN(application.final_score)
        ? Number(application.final_score).toFixed(2).replace('.', ',')
        : null;
      analysisStep = {
        status: 'completed',
        label: ordemProc ? 'Inscrição Deferida' : 'Análise Aprovada',
        description: mediaFinal ? 'Inscrição deferida.' : 'Inscrição deferida com sucesso',
        mediaFinal: mediaFinal,
        color: '#10B981',
        icon: '✅',
        bg: '#F0FDF4'
      };
    } else if (application.analysis_status === 'rejected') {
      const reasonTitle = application.curricular_rejection_reason_title;
      const reason = (application.rejection_reason || application.curricular_rejection_reason || '').toString().trim();
      let desc = 'Inscrição reprovada na análise';
      if (reasonTitle) {
        desc = reasonTitle;
      } else if (reason) {
        desc = /^\d+$/.test(reason) ? (getRejectionReasonDescription(reason) || reason) : reason;
      }
      analysisStep = {
        status: 'rejected',
        label: 'Análise Reprovada',
        description: desc,
        color: '#EF4444',
        icon: '❌',
        bg: '#FEF2F2'
      };
    }
    
    return {
      payment: paymentStep,
      analysis: analysisStep,
      isProva: Boolean(isProvaProcess),
      provaAttendance: provaAttendanceStep,
      provaEnrollment: provaEnrollmentStep,
      overallStatus: application.status
    };
  }, [processes, getPaymentStatus, getRejectionReasonDescription]);

  // Funções de manipulação
  const handleNewApplication = useCallback((process) => {
    if (getProcessRegistrationStatus(process) !== 'open') {
      addNotification(
        'Aviso',
        `As inscrições para este processo começam em ${formatDate(process.start_date)}.`,
        'warning'
      );
      return;
    }
    setSelectedProcess(process);
    setFormData({ ...INITIAL_APPLICATION_FORM });
    setProcessQuestionnaire(null);
    setShowApplicationModal(true);
  }, [addNotification]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Se o curso foi alterado, resetar a cota
      if (field === 'course_id') {
        newData.quota_type = '';
      }
      
      return newData;
    });
    if (error) setError('');
  }, [error]);

  const handleQuestionnaireAnswer = useCallback((questionId, alternativeId) => {
    setFormData(prev => ({
      ...prev,
      questionnaireAnswers: { ...(prev.questionnaireAnswers || {}), [String(questionId)]: String(alternativeId) }
    }));
    if (error) setError('');
  }, [error]);

  const unansweredQuestionnaireItems = useMemo(
    () =>
      !formData.isCourseChange && processQuestionnaire
        ? getUnansweredQuestionnaireQuestions(processQuestionnaire, formData.questionnaireAnswers)
        : [],
    [formData.isCourseChange, processQuestionnaire, formData.questionnaireAnswers]
  );

  const unansweredQuestionnaireIdSet = useMemo(() => {
    const s = new Set();
    unansweredQuestionnaireItems.forEach((q) => {
      s.add(q.id);
      s.add(String(q.id));
    });
    return s;
  }, [unansweredQuestionnaireItems]);

  // Rolar até o topo do formulário quando houver erro de validação (candidato vê o aviso)
  useEffect(() => {
    if (error && showApplicationModal && formTopRef.current) {
      formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [error, showApplicationModal]);

  const handleExemptionChange = useCallback((requestExemption) => {
    setFormData(prev => {
      if (prev.isCourseChange) return prev;
      return {
        ...prev,
        requestExemption,
        exemptionDocuments: requestExemption ? prev.exemptionDocuments : [],
        exemptionNis: requestExemption ? prev.exemptionNis : '',
        exemptionReason: requestExemption ? prev.exemptionReason : ''
      };
    });
  }, []);

  const handleCurricularDocumentUpload = useCallback((document) => {
    console.log('📤 Adicionando documento curricular:', document);
    const reject = candidateDocumentRejectReason(document);
    if (reject) {
      setError(reject);
      return;
    }
    setFormData(prev => ({
      ...prev,
      curricularDocuments: [...prev.curricularDocuments, document]
    }));
    if (error) setError('');
  }, [error]);

  const handleCurricularDocumentRemove = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      curricularDocuments: prev.curricularDocuments.filter((_, i) => i !== index)
    }));
  }, []);

  const handleExemptionDocumentUpload = useCallback((document) => {
    const reject = candidateDocumentRejectReason(document);
    if (reject) {
      setError(reject);
      return;
    }
    setFormData(prev => {
      if (prev.isCourseChange) return prev;
      return {
        ...prev,
        exemptionDocuments: [...prev.exemptionDocuments, document]
      };
    });
    if (error) setError('');
  }, [error]);

  const handleExemptionDocumentRemove = useCallback((index) => {
    setFormData(prev => {
      if (prev.isCourseChange) return prev;
      return {
        ...prev,
        exemptionDocuments: prev.exemptionDocuments.filter((_, i) => i !== index)
      };
    });
  }, []);

  const handleSpecialAttendanceChange = useCallback((requestSpecialAttendance) => {
    setFormData(prev => {
      if (prev.isCourseChange) return prev;
      return {
        ...prev,
        requestSpecialAttendance,
        specialAttendanceTypeIds: requestSpecialAttendance ? prev.specialAttendanceTypeIds : [],
        specialAttendanceDocuments: requestSpecialAttendance ? prev.specialAttendanceDocuments : [],
        specialAttendanceJustification: requestSpecialAttendance ? prev.specialAttendanceJustification : ''
      };
    });
  }, []);

  const toggleSpecialAttendanceType = useCallback((typeId) => {
    const idNum = Number(typeId);
    setFormData(prev => {
      if (prev.isCourseChange) return prev;
      const set = new Set((prev.specialAttendanceTypeIds || []).map(Number));
      if (set.has(idNum)) set.delete(idNum);
      else set.add(idNum);
      return { ...prev, specialAttendanceTypeIds: [...set] };
    });
    if (error) setError('');
  }, [error]);

  const handleSpecialAttendanceJustificationChange = useCallback((value) => {
    setFormData(prev => (prev.isCourseChange ? prev : { ...prev, specialAttendanceJustification: value }));
    if (error) setError('');
  }, [error]);

  const handleSpecialAttendanceDocumentUpload = useCallback((document) => {
    const reject = candidateDocumentRejectReason(document);
    if (reject) {
      setError(reject);
      return;
    }
    setFormData(prev => {
      if (prev.isCourseChange) return prev;
      return { ...prev, specialAttendanceDocuments: [...prev.specialAttendanceDocuments, document] };
    });
    if (error) setError('');
  }, [error]);

  const handleSpecialAttendanceDocumentRemove = useCallback((index) => {
    setFormData(prev => {
      if (prev.isCourseChange) return prev;
      return { ...prev, specialAttendanceDocuments: prev.specialAttendanceDocuments.filter((_, i) => i !== index) };
    });
  }, []);

  // Enviar inscrição
  const handleSubmitApplication = useCallback(async (e) => {
    e.preventDefault();
    
    console.log('🚀 handleSubmitApplication chamado com:', { 
      formData, 
      selectedProcess,
      isCourseChange: formData.isCourseChange,
      originalApplicationId: formData.originalApplicationId,
      course_id: formData.course_id,
      quota_type: formData.quota_type
    });
    
    // Verificar se formData está correto
    console.log('🔍 VERIFICAÇÃO - formData.isCourseChange:', formData.isCourseChange);
    console.log('🔍 VERIFICAÇÃO - formData.originalApplicationId:', formData.originalApplicationId);
    console.log('🔍 VERIFICAÇÃO - formData.course_id:', formData.course_id);
    console.log('🔍 VERIFICAÇÃO - formData.quota_type:', formData.quota_type);
    console.log('🔍 VERIFICAÇÃO - selectedProcess:', selectedProcess);
    
    // Validação unificada: coletar tudo que falta e exibir aviso claro
    if (!selectedProcess) {
      setError('Todos os campos são obrigatórios, revise suas respostas e veja o que ficou sem preenchimento.');
      return;
    }
    const missing = [];
    let firstUnansweredQuestionnaireId = null;
    if (!formData.course_id) missing.push('curso');
    if (selectedProcess?.use_quotas && !formData.quota_type) missing.push('cota');
    if (!formData.isCourseChange && selectedProcess?.selection_mode === 'prova' && selectedProcess?.exam_allocation_policy === 'candidate_choice' && !formData.exam_preferred_venue_id) {
      missing.push('local de prova');
    }
    if (!formData.isCourseChange) {
      const isLetterProcess = isIntentionLetterMode(selectedProcess.selection_mode);
      if (isLetterProcess) {
        const letterCourse = courses.find((c) => Number(c.id) === Number(formData.course_id));
        const letterCheck = validateIntentionLetterForCourse(letterCourse, formData.intentionLetterText);
        if (!letterCheck.ok) {
          missing.push('carta de intenção');
        }
      } else if (selectedProcess.selection_mode !== 'prova' && formData.curricularDocuments.length === 0) {
        missing.push('documentos curriculares');
      }
      if (formData.requestExemption && selectedProcess.has_fee) {
        const cadunicoMode = Boolean(selectedProcess.exemption_cadunico_consulta);
        if (cadunicoMode) {
          const nisDigits = String(formData.exemptionNis || '').replace(/\D/g, '');
          if (nisDigits.length !== 11) missing.push('NIS (11 dígitos)');
        } else if (formData.exemptionDocuments.length === 0) {
          missing.push('documentos de isenção');
        }
      }
      if (processQuestionnaire) {
        const unanswered = getUnansweredQuestionnaireQuestions(processQuestionnaire, formData.questionnaireAnswers);
        if (unanswered.length > 0) {
          missing.push('questionário socioeconômico (perguntas sem resposta)');
          firstUnansweredQuestionnaireId = unanswered[0].id;
        }
      }
      if (formData.requestSpecialAttendance) {
        if (!processOffersSpecialAttendance(selectedProcess)) {
          setError('Este processo não permite pedido de atendimento especial.');
          return;
        }
        if (!isInSpecialAttendanceRequestPeriod(selectedProcess)) {
          setError('Fora do período para solicitar atendimento especial. O prazo para o pedido já encerrou.');
          return;
        }
        if (!formData.specialAttendanceTypeIds || formData.specialAttendanceTypeIds.length === 0) {
          missing.push('tipos de atendimento especial');
        }
        const needsSaDoc = (formData.specialAttendanceTypeIds || []).some((tid) => {
          const t = specialAttendanceTypesForModal.find(x => Number(x.id) === Number(tid));
          return t && t.requires_attachment;
        });
        if (needsSaDoc && (!formData.specialAttendanceDocuments || formData.specialAttendanceDocuments.length === 0)) {
          missing.push('documentos de atendimento especial');
        }
      }
    }
    if (!formData.isCourseChange && formData.requestExemption && selectedProcess.has_fee && !isInExemptionPeriod(selectedProcess)) {
      setError('Fora do período para solicitar isenção. O prazo para pedido de isenção já encerrou.');
      return;
    }
    if (missing.length > 0) {
      setError('Todos os campos são obrigatórios, revise suas respostas e veja o que ficou sem preenchimento: ' + missing.join(', ') + '.');
      if (firstUnansweredQuestionnaireId != null) {
        window.setTimeout(() => {
          document.getElementById(`questionnaire-q-${firstUnansweredQuestionnaireId}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 350);
      }
      return;
    }

    console.log('✅ Validações passaram, iniciando processamento...');
    setLoading(true);
    setError('');
    
    try {
            if (formData.isCourseChange) {
        console.log('🔄 Iniciando lógica de troca de curso...');
        console.log('📋 Dados da troca:', {
          originalApplicationId: formData.originalApplicationId,
          newCourseId: formData.course_id,
          applications: applications.length,
          courses: courses.length,
          formData: formData
        });
        
        // Verificar se originalApplicationId está definido
        if (!formData.originalApplicationId) {
          console.error('❌ originalApplicationId não está definido no formData');
          console.error('❌ formData completo:', formData);
          throw new Error('ID da inscrição original não foi definido');
        }
        
        // LÓGICA DE TROCA DE CURSO
        console.log('🔍 Procurando inscrição original com ID:', formData.originalApplicationId);
        console.log('🔍 Todas as inscrições disponíveis:', applications);
        
        const originalApplication = applications.find(app => app.id === formData.originalApplicationId);
        console.log('🔍 Inscrição original encontrada:', originalApplication);
        
        // 🚨 DEBUG CRÍTICO - Verificar dados da aplicação original ANTES de qualquer processamento
        console.log('🚨 DADOS ORIGINAIS DA APLICAÇÃO (ANTES DE QUALQUER ALTERAÇÃO):');
        console.log('  📋 ID:', originalApplication?.id);
        console.log('  🎓 Course ID:', originalApplication?.course_id, 'tipo:', typeof originalApplication?.course_id);
        console.log('  🎓 Course Name:', originalApplication?.course_name);
        console.log('  📅 Created At:', originalApplication?.created_at);
        console.log('  📅 Updated At:', originalApplication?.updated_at);
        
        if (!originalApplication) {
          console.error('❌ Inscrição original não encontrada para ID:', formData.originalApplicationId);
          throw new Error(`Inscrição original não encontrada para ID: ${formData.originalApplicationId}`);
        }
        
        console.log('🔍 Buscando cursos:', {
          coursesDisponiveis: courses?.length || 0,
          coursesIds: courses?.map(c => ({ id: c.id, name: c.name })) || [],
          originalApplicationCourseId: originalApplication.course_id,
          formDataCourseId: formData.course_id
        });
        
        console.log('🔍 DEBUG - courses array completo:', courses);
        console.log('🔍 DEBUG - originalApplication.course_id:', originalApplication.course_id, 'tipo:', typeof originalApplication.course_id);
        console.log('🔍 DEBUG - formData.course_id:', formData.course_id, 'tipo:', typeof formData.course_id);
        
        // Converter IDs para números para evitar problemas de tipo
        const oldCourseId = Number(originalApplication.course_id);
        const newCourseId = Number(formData.course_id);
        
        console.log('🔍 IDs convertidos:', { oldCourseId, newCourseId, tipos: { old: typeof oldCourseId, new: typeof newCourseId } });
        console.log('🔍 DEBUG - Todos os cursos disponíveis:', courses.map(c => ({ id: c.id, name: c.name, process_id: c.process_id })));
        console.log('🔍 DEBUG - Buscando oldCourse com ID:', oldCourseId);
        console.log('🔍 DEBUG - Buscando newCourse com ID:', newCourseId);
        
        const oldCourse = courses.find(c => c.id === oldCourseId);
        const newCourse = courses.find(c => c.id === newCourseId);
        
        console.log('🔍 DEBUG - oldCourse encontrado:', oldCourse);
        console.log('🔍 DEBUG - newCourse encontrado:', newCourse);
        console.log('🔍 DEBUG - Comparação de IDs:', {
          oldCourseId,
          newCourseId,
          oldCourseIdType: typeof oldCourseId,
          newCourseIdType: typeof newCourseId,
          coursesIds: courses.map(c => ({ id: c.id, type: typeof c.id }))
        });
        
        console.log('🔍 Dados encontrados:', { 
          originalApplication, 
          oldCourse, 
          newCourse,
          originalApplicationId: formData.originalApplicationId,
          courseId: formData.course_id
        });
        
        if (!oldCourse || !newCourse) {
          console.error('❌ Cursos não encontrados:', { oldCourse, newCourse });
          throw new Error('Cursos não encontrados');
        }

        // Verificar se matriz curricular é igual (séries, disciplinas, pesos) — não se aplica a ordem de inscrição
        let matrixCheck;
        if (isOrdemInscricaoMode(selectedProcess.selection_mode)) {
          matrixCheck = { same_matrix: true, message: 'Processo por ordem de inscrição' };
        } else {
        try {
          matrixCheck = await apiClient.compareCourseMatrices(oldCourseId, newCourseId);
        } catch (err) {
          console.error('Erro ao comparar matrizes:', err);
          addNotification('Erro ao verificar matriz curricular. Tente novamente.', 'error');
          return;
        }
        }
        if (!matrixCheck.same_matrix) {
          // Matriz / configuração de prova diferente: excluir inscrição e criar nova (preserva pagamento)
          const isProvaProcess = selectedProcess.selection_mode === 'prova';
          const hasCurricularDocs = formData.curricularDocuments?.length > 0;
          if (!isProvaProcess && !hasCurricularDocs) {
            addNotification('Para trocar para um curso com matriz diferente, é necessário enviar os documentos curriculares.', 'warning');
            return;
          }
          const { data: newApp, error: exErr } = await apiClient.excludeAndReapply(originalApplication.id, {
            course_id: newCourseId,
            quota_type: formData.quota_type || 'AC',
            curricularDocuments: isProvaProcess ? [] : formData.curricularDocuments,
            exemptionDocuments: [],
            exemptionReason: ''
          });
          if (exErr) {
            addNotification(exErr, 'error');
            return;
          }
          const { error: ccError } = await apiClient.createCourseChange({
            application_id: originalApplication.id,
            user_id: user.id,
            process_id: selectedProcess.id,
            from_course_id: oldCourseId,
            to_course_id: newCourseId
          });
          if (ccError) console.warn('Histórico não salvo:', ccError);
          const refreshed = await apiClient.getApplications();
          setApplications(Array.isArray(refreshed) ? removeDuplicateApplications(refreshed) : []);
          const refreshedCourseChanges = await apiClient.getCourseChanges();
          setCourseChanges(Array.isArray(refreshedCourseChanges) ? refreshedCourseChanges : []);
          setShowApplicationModal(false);
          setFormData({ ...INITIAL_APPLICATION_FORM });
          setSelectedProcess(null);
          addNotification('Inscrição excluída e nova inscrição criada. O pagamento foi preservado.', 'success');
          return;
        }
        
        // Matriz igual: troca simples (apenas curso/campus)
        // Criar registro de alteração de curso
        const courseChange = {
          id: Date.now(),
          application_id: originalApplication.id,
          user_id: user.id,
          process_id: selectedProcess.id,
          from_course_id: oldCourse.id,
          to_course_id: newCourse.id,
          old_course_id: oldCourse.id,
          old_course_name: oldCourse.name,
          old_course_campus: oldCourse.campus,
          new_course_id: newCourse.id,
          new_course_name: newCourse.name,
          new_course_campus: newCourse.campus,
          changed_at: new Date().toISOString()
        };
        
        console.log('🔍 DEBUG - courseChange criado:', courseChange);
        console.log('🔍 DEBUG - Verificação dos nomes:', {
          oldCourseName: oldCourse.name,
          newCourseName: newCourse.name,
          oldCourseId: oldCourse.id,
          newCourseId: newCourse.id
        });
        
        // 🔍 DEBUG CRÍTICO - Verificar se os cursos estão corretos ANTES de criar o registro
        console.log('🚨 DEBUG CRÍTICO - Verificação antes de salvar:');
        console.log('  📋 Aplicação original:', {
          id: originalApplication.id,
          course_id: originalApplication.course_id,
          course_name: originalApplication.course_name,
          created_at: originalApplication.created_at,
          updated_at: originalApplication.updated_at
        });
        console.log('  🎓 Curso antigo encontrado:', {
          id: oldCourse.id,
          name: oldCourse.name,
          process_id: oldCourse.process_id
        });
        console.log('  🎓 Curso novo encontrado:', {
          id: newCourse.id,
          name: newCourse.name,
          process_id: newCourse.process_id
        });
        console.log('  🔄 Registro que será criado:', {
          old_course_name: oldCourse.name,
          new_course_name: newCourse.name,
          old_course_id: oldCourse.id,
          new_course_id: newCourse.id
        });
        
        // Verificar se já existe uma aplicação para o mesmo curso
        const existingApplicationForCourse = applications.find(app => 
          app.user_id === user.id && 
          app.course_id === newCourse.id && 
          app.process_id === selectedProcess.id
        );
        
        if (existingApplicationForCourse) {
          console.log('⚠️ Já existe uma aplicação para este curso:', existingApplicationForCourse);
          addNotification('Você já possui uma inscrição para este curso', 'warning');
          
          // Fechar modal e resetar formulário mesmo quando há erro
          setShowApplicationModal(false);
          setFormData({ ...INITIAL_APPLICATION_FORM });
      setSelectedProcess(null);
      return;
    }
    
    // CRIAR NOVA INSCRIÇÃO com o novo curso (seguindo lições aprendidas)
        const newApplication = {
          id: originalApplication.id, // MANTER o mesmo ID da aplicação original
          user_id: user.id,
          process_id: selectedProcess.id,
          course_id: newCourse.id,
          course_name: newCourse.name,
          candidate_id: user.id,
          candidate_name: user.name,
          candidate_cpf: user.cpf || 'N/A',
          candidate_email: user.email,
          quota_type: originalApplication.quota_type || 'AC',
          status: 'pending', // Nova análise necessária
          analysis_status: 'pending',
          // PRESERVAR status de isenção se já foi aprovado
          exemption_status: originalApplication.exemption_status === 'approved' ? 'approved' : 
                           (formData.requestExemption ? 'pending' : originalApplication.exemption_status),
          exemption_reason: formData.requestExemption ? formData.exemptionReason : originalApplication.exemption_reason,
          exemption_documents: formData.requestExemption ? formData.exemptionDocuments : originalApplication.exemption_documents,
          // PRESERVAR status de pagamento se já foi confirmado
          payment_status: originalApplication.payment_status === 'paid' ? 'paid' :
                         originalApplication.payment_status === 'exempted' ? 'exempted' :
                         (formData.requestExemption ? 'exempt' : originalApplication.payment_status),
          curricular_documents: originalApplication.curricular_documents || [],
          is_exemption_requested: originalApplication.is_exemption_requested || false,
          analyst_id: null,
          analyst_name: null,
          assigned_at: null,
          rejection_reason: null,
          uploaded_files: originalApplication.uploaded_files || [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        console.log('🔄 Criando nova inscrição:', newApplication);
        console.log('📝 Histórico de alteração:', courseChange);
        
        // Adicionar ao histórico local
        setCourseChanges(prev => {
          console.log('📋 Adicionando ao histórico de alterações:', courseChange);
          return [...prev, courseChange];
        });
        
        // 🔒 TRANSAÇÃO ATÔMICA - Todas as operações devem ser bem-sucedidas
        console.log('🔒 Iniciando transação atômica para troca de curso...');
        
        try {
          // 1. Salvar histórico de alteração
          console.log('📝 Passo 1: Salvando histórico de alteração...');
          const { error: ccError } = await apiClient.createCourseChange({
            application_id: originalApplication.id,
            user_id: user.id,
            process_id: selectedProcess.id,
            from_course_id: originalApplication.course_id,
            to_course_id: newCourse.id
          });
          if (ccError) throw new Error(`Erro ao salvar histórico: ${ccError}`);
          console.log('✅ Histórico salvo com sucesso');
          
          // 2. Atualizar inscrição existente via API
          console.log('📝 Passo 2: Atualizando inscrição existente...');
          const { error: updateError } = await apiClient.updateApplication(originalApplication.id, {
            course_id: newCourse.id,
            quota_type: formData.quota_type || 'AC'
          });
          if (updateError) {
            throw new Error(`Erro ao atualizar inscrição: ${updateError}`);
          }
          console.log('✅ Inscrição atualizada com sucesso');
          
          // 3. Recarregar dados para garantir consistência
          console.log('📝 Passo 3: Recarregando dados...');
          const refreshed = await apiClient.getApplications();
          setApplications(Array.isArray(refreshed) ? removeDuplicateApplications(refreshed) : []);
          const refreshedCourseChanges = await apiClient.getCourseChanges();
          setCourseChanges(Array.isArray(refreshedCourseChanges) ? refreshedCourseChanges : []);
          console.log('✅ Estado local atualizado com sucesso');
          
          // 4. Verificação final de consistência
          console.log('🔍 Verificação final de consistência...');
          const allApps = await apiClient.getApplications();
          const savedApp = Array.isArray(allApps) ? allApps.find(x => x.id === originalApplication.id) : null;
          if (savedApp) {
            console.log('📋 Aplicação atualizada no banco:', {
              id: savedApp.id,
              course_id: savedApp.course_id,
              course_name: savedApp.course_name,
              updated_at: savedApp.updated_at
            });
            
            if (Number(savedApp.course_id) !== Number(newCourse.id)) {
              throw new Error(`INCONSISTÊNCIA CRÍTICA: Curso salvo (${savedApp.course_id}) não confere com esperado (${newCourse.id})`);
            }
            console.log('✅ Verificação de consistência passou!');
          }
          
        } catch (transactionError) {
          console.error('❌ ERRO NA TRANSAÇÃO:', transactionError);
          console.error('🔄 Tentando rollback...');
          
          // ROLLBACK: Reverter estado local se necessário
          setApplications(prev => prev); // Manter estado atual
          
          throw new Error(`Falha na transação de troca de curso: ${transactionError.message}`);
        }
        
        addNotification('Curso alterado com sucesso!', 'success');
        
      } else {
        // LÓGICA DE NOVA INSCRIÇÃO
        const newApplication = {
          id: Date.now(),
          user_id: user.id,
          process_id: selectedProcess.id,
          course_id: formData.course_id,
          course_name: courses.find(c => c.id === Number(formData.course_id))?.name || 'Curso não encontrado',
          candidate_id: user.id,
          candidate_name: user.name,
          candidate_cpf: user.cpf || 'N/A',
          candidate_email: user.email,
          quota_type: formData.quota_type || 'AC',
          payment_status: formData.requestExemption ? 'pending_exemption' : 'pending',
          status: formData.requestExemption ? 'pending_exemption' : 'pending',
          analyst_id: null,
          analyst_name: null,
          assigned_at: null,
          is_exemption_requested: formData.requestExemption || false,
          exemption_status: formData.requestExemption ? 'pending' : null,
          exemption_reason: formData.exemptionReason || null,
          rejection_reason: null,
          curricular_documents: selectedProcess.selection_mode === 'prova' ? [] : formData.curricularDocuments,
          exemption_documents: formData.requestExemption ? formData.exemptionDocuments : [],
          created_at: new Date().toISOString()
        };
        
        console.log('📝 Nova inscrição:', newApplication);
        
        const payload = {
          process_id: selectedProcess.id,
          course_id: formData.course_id,
          exam_preferred_venue_id: formData.exam_preferred_venue_id || null,
          quota_type: formData.quota_type || 'AC',
          request_exemption: formData.requestExemption || false,
          exemption_nis: formData.requestExemption ? String(formData.exemptionNis || '').replace(/\D/g, '') : undefined,
          curricularDocuments: isIntentionLetterMode(selectedProcess.selection_mode) || selectedProcess.selection_mode === 'prova'
            ? []
            : formData.curricularDocuments,
          intentionLetterText: isIntentionLetterMode(selectedProcess.selection_mode)
            ? formData.intentionLetterText
            : undefined,
          exemptionDocuments: formData.requestExemption ? formData.exemptionDocuments : [],
          exemptionReason: formData.exemptionReason || null
        };
        if (processQuestionnaire && formData.questionnaireAnswers) {
          payload.questionnaire_answers = formData.questionnaireAnswers;
        }
        const { data: created, error: createError } = await apiClient.createApplication(payload);
        if (createError) throw new Error(createError);
        if (formData.requestSpecialAttendance && processOffersSpecialAttendance(selectedProcess) && isInSpecialAttendanceRequestPeriod(selectedProcess)) {
          const saDocuments = (formData.specialAttendanceDocuments || [])
            .filter((d) => d && (d.url || d.name))
            .map((d) => ({
              url: d.url || '',
              name: d.name || 'documento_atendimento_especial',
              type: d.type || null,
              size: d.size || null,
              uploadedAt: d.uploadedAt || null,
            }))
            .filter((d) => d.url);
          const { error: saErr } = await apiClient.saveSpecialAttendanceApplication(created.id, {
            type_ids: (formData.specialAttendanceTypeIds || []).map(Number),
            documents: saDocuments,
            justification: (formData.specialAttendanceJustification || '').trim()
          });
          if (saErr) {
            addNotification('Aviso', `Inscrição criada, mas o pedido de atendimento especial não foi salvo: ${saErr}`, 'warning');
          }
        }
        // Recarregar inscrições: o POST da inscrição não inclui o pedido de atendimento especial (gravado em PUT separado);
        // sem isso o selo "Atendimento especial" não aparece até F5 ou próximo poll.
        try {
          const refreshed = await apiClient.getApplications();
          setApplications(Array.isArray(refreshed) ? removeDuplicateApplications(refreshed) : []);
        } catch {
          setApplications((prev) => [
            ...prev,
            { ...created, course_name: courses.find((c) => c.id === Number(formData.course_id))?.name },
          ]);
        }
        addNotification('Inscrição enviada com sucesso!', 'success');
      }
      
      // Fechar modal e resetar formulário
      setShowApplicationModal(false);
      setFormData({ ...INITIAL_APPLICATION_FORM });
      setSelectedProcess(null);
      
    } catch (error) {
      console.error('❌ Erro ao enviar inscrição:', error);
      setError(error.message || 'Erro ao enviar inscrição');
      
      // Fechar modal e resetar formulário mesmo em caso de erro
      setShowApplicationModal(false);
      setFormData({ ...INITIAL_APPLICATION_FORM });
      setSelectedProcess(null);
    } finally {
      setLoading(false);
    }
  }, [formData, selectedProcess, courses, applications, user, addNotification, processQuestionnaire, isInExemptionPeriod, isInSpecialAttendanceRequestPeriod, processOffersSpecialAttendance, specialAttendanceTypesForModal, removeDuplicateApplications]);

  const getCurricularRejectionObservation = useCallback((application) => {
    if (!application) return null;
    const analystNotes = (application.curricular_rejection_notes || application.rejection_notes || '').trim();
    if (analystNotes) return { text: analystNotes, kind: 'analyst' };
    const catalogDesc = (application.curricular_rejection_reason_description || '').trim();
    if (catalogDesc) return { text: catalogDesc, kind: 'catalog' };
    return null;
  }, []);

  // Função para abrir modal de upload de novo documento (curricular - análise reprovada)
  const handleUploadNewDocument = useCallback(async (application) => {
    setNewDocuments([]);
    setDocumentUploadError('');
    setDocumentFileUploading(false);
    setSelectedApplicationForUpload(application);
    setShowDocumentUploadModal(true);
    try {
      const fresh = await apiClient.getApplication(application.id);
      if (fresh && fresh.id) setSelectedApplicationForUpload(fresh);
    } catch {
      /* mantém dados da lista */
    }
  }, []);

  // Função para abrir modal de reenvio de pedido de isenção (isenção indeferida)
  const handleResendExemption = useCallback((application) => {
    setSelectedApplicationForResendExemption(application);
    setResendExemptionDocs([]);
    setResendExemptionNis(String(application?.exemption_nis || '').replace(/\D/g, '').slice(0, 11));
    setResendExemptionError('');
    setShowResendExemptionModal(true);
  }, []);

  const handleSaveResendExemption = useCallback(async () => {
    if (!selectedApplicationForResendExemption) return;
    const proc = (typeof processes !== 'undefined' ? processes : []).find(p => Number(p.id) === Number(selectedApplicationForResendExemption.process_id))
      || null;
    const cadunicoMode = Boolean(proc?.exemption_cadunico_consulta);
    const nisDigits = String(resendExemptionNis || '').replace(/\D/g, '');
    if (cadunicoMode) {
      if (nisDigits.length !== 11 && resendExemptionDocs.length === 0) {
        setResendExemptionError('Informe o NIS (11 dígitos) ou envie pelo menos um documento.');
        addNotification('Atenção', 'Informe NIS ou documento de isenção', 'warning');
        return;
      }
      if (nisDigits && nisDigits.length !== 11) {
        setResendExemptionError('NIS inválido. Informe 11 dígitos.');
        addNotification('Atenção', 'NIS inválido', 'warning');
        return;
      }
    } else if (resendExemptionDocs.length === 0) {
      setResendExemptionError('Selecione pelo menos um documento de isenção antes de enviar.');
      addNotification('Atenção', 'Selecione pelo menos um documento de isenção', 'warning');
      return;
    }
    try {
      setActionSubmitting(true);
      setResendExemptionError('');
      const { error } = await apiClient.resendExemption(selectedApplicationForResendExemption.id, resendExemptionDocs, { exemption_nis: nisDigits || undefined });
      if (error) throw new Error(error);
      await loadData(true);
      addNotification('Sucesso', 'Pedido de isenção reenviado! Aguarde a nova análise.', 'success');
      setShowResendExemptionModal(false);
      setSelectedApplicationForResendExemption(null);
      setResendExemptionDocs([]);
      setResendExemptionError('');
    } catch (err) {
      const msg = friendlyNetworkError(err.message || 'Erro ao reenviar pedido. Tente novamente.');
      setResendExemptionError(msg);
      addNotification('Erro', msg, 'error');
    } finally {
      setActionSubmitting(false);
    }
  }, [selectedApplicationForResendExemption, resendExemptionDocs, resendExemptionNis, processes, addNotification, loadData]);

  const handleResendSpecialAttendance = useCallback((application) => {
    setSelectedApplicationForResendSpecialAttendance(application);
    setResendSpecialAttendanceDocs([]);
    setResendSpecialAttendanceError('');
    setShowResendSpecialAttendanceModal(true);
  }, []);

  const handleSaveResendSpecialAttendance = useCallback(async () => {
    const app = selectedApplicationForResendSpecialAttendance;
    if (!app || resendSpecialAttendanceDocs.length === 0) {
      setResendSpecialAttendanceError('Selecione pelo menos um documento antes de enviar.');
      addNotification('Atenção', 'Selecione pelo menos um documento', 'warning');
      return;
    }
    try {
      setActionSubmitting(true);
      setResendSpecialAttendanceError('');
      const typeIds = Array.isArray(app.special_attendance_type_ids) ? app.special_attendance_type_ids.map((x) => Number(x)).filter((n) => !Number.isNaN(n)) : [];
      const { error } = await apiClient.saveSpecialAttendanceApplication(app.id, {
        type_ids: typeIds,
        justification: (app.special_attendance_justification || '').trim() || undefined,
        documents: resendSpecialAttendanceDocs,
      });
      if (error) throw new Error(error);
      await loadData(true);
      addNotification('Sucesso', 'Pedido de atendimento especial reenviado. Aguarde nova análise.', 'success');
      setShowResendSpecialAttendanceModal(false);
      setSelectedApplicationForResendSpecialAttendance(null);
      setResendSpecialAttendanceDocs([]);
      setResendSpecialAttendanceError('');
    } catch (err) {
      const msg = friendlyNetworkError(err.message || 'Erro ao reenviar pedido. Tente novamente.');
      setResendSpecialAttendanceError(msg);
      addNotification('Erro', msg, 'error');
    } finally {
      setActionSubmitting(false);
    }
  }, [selectedApplicationForResendSpecialAttendance, resendSpecialAttendanceDocs, addNotification, loadData]);

  const handleAppeal = useCallback((application, type = 'exemption') => {
    setSelectedApplicationForAppeal(application);
    setAppealType(type);
    setAppealForm({ reason: '', documents: [], justification: '', question_number: '' });
    setAppealFormErrors({});
    setAppealSubmitError('');
    setAnswerKeyQuestionHint(null);
    setAnswerKeyHintError('');
    setShowAppealModal(true);
  }, []);

  useEffect(() => {
    if (!showAppealModal || appealType !== 'answer_key' || !selectedApplicationForAppeal) {
      setAnswerKeyQuestionHint(null);
      setAnswerKeyHintError('');
      return undefined;
    }
    const qn = parseInt(appealForm.question_number, 10);
    if (!Number.isFinite(qn) || qn < 1) {
      setAnswerKeyQuestionHint(null);
      setAnswerKeyHintError('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setAnswerKeyHintLoading(true);
      setAnswerKeyHintError('');
      const { data, error } = await apiClient.getAnswerKeyAppealQuestionHint(
        selectedApplicationForAppeal.id,
        qn
      );
      if (cancelled) return;
      setAnswerKeyHintLoading(false);
      if (error) {
        setAnswerKeyQuestionHint(null);
        setAnswerKeyHintError(error);
        return;
      }
      setAnswerKeyQuestionHint(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    showAppealModal,
    appealType,
    selectedApplicationForAppeal,
    appealForm.question_number,
  ]);

  const handleViewAppeal = useCallback((application) => {
    setSelectedApplicationForAppealView(application);
    setAppealViewTabIndex(0);
    setShowAppealViewModal(true);
  }, []);

  const handlePrintAppeal = useCallback(() => {
    const el = document.getElementById('appeal-print-content');
    if (!el) return;
    const win = window.open('', '_blank');
    win.document.write(`
      <!DOCTYPE html><html><head><meta charset="utf-8"><title>Recurso - Ingressa</title>
      <style>body{font-family:sans-serif;padding:24px;max-width:800px;margin:0 auto;line-height:1.5}
      h1{font-size:18px;border-bottom:1px solid #ccc;padding-bottom:8px}
      .section{margin:16px 0}.label{font-weight:600;color:#374151}.value{margin-top:4px}
      ul{margin:4px 0;padding-left:20px}</style></head><body>
      ${el.innerHTML}
      </body></html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 250);
  }, []);

  const handleSaveAppealToFile = useCallback((tabIndex = 0) => {
    const app = selectedApplicationForAppealView;
    const allAppeals = [
      app?.appeal && { ...app.appeal, appeal_type: app.appeal.appeal_type || 'exemption' },
      app?.list_appeal && { ...app.list_appeal, appeal_type: 'list' },
      app?.result_appeal && { ...app.result_appeal, appeal_type: 'preliminary_result' },
      app?.special_attendance_appeal && { ...app.special_attendance_appeal, appeal_type: 'special_attendance' },
      ...getAnswerKeyAppealsList(app).map((a) => ({ ...a, appeal_type: 'answer_key' })),
    ].filter(Boolean);
    const appealData = allAppeals[tabIndex] || allAppeals[0];
    if (!appealData) return;
    const process = processes.find(p => p.id === app.process_id);
    const course = courses.find(c => c.id === app.course_id);
    const docs = (appealData.documents || []).map((d, i) => `${i + 1}. ${d.name || d.url || 'Documento'}`).join('\n');
    const officialLines = appealData.candidate_official_response_visible
      ? [
          '',
          '--- RESPOSTA OFICIAL (publicada no portal) ---',
          `Decisão: ${appealData.official_decision === 'approved' ? 'Deferido' : appealData.official_decision === 'rejected' ? 'Indeferido' : '—'}`,
          'Fundamentação:',
          appealData.official_fundamentacao || '(não informada)',
        ]
      : [];
    const content = [
      'RECURSO - INGRESSA',
      '====================',
      '',
      `Processo: ${process?.name || '-'}`,
      `Curso: ${course?.name || '-'}`,
      `Data do envio: ${appealData.created_at ? formatDate(appealData.created_at) : '-'}`,
      `Status: ${appealData.status === 'pending' ? 'Aguardando análise' : (appealData.status === 'approved' || appealData.status === 'appeal_approved') ? 'Aprovado' : (appealData.status === 'rejected' || appealData.status === 'appeal_rejected') ? 'Indeferido' : appealData.status}`,
      ...officialLines,
      '',
      'JUSTIFICATIVA:',
      appealData.justification || '(não informada)',
      '',
      'DOCUMENTOS ANEXADOS:',
      docs || '(nenhum)',
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const typeSuffix = { exemption: 'isenção', list: 'lista', preliminary_result: 'resultado-preliminar', special_attendance: 'atendimento-especial', answer_key: 'gabarito' }[appealData.appeal_type] || 'recurso';
    a.download = `recurso-${typeSuffix}-inscricao-${app.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    addNotification('Sucesso', 'Arquivo salvo', 'success');
  }, [selectedApplicationForAppealView, processes, courses, addNotification]);

  const handleSaveAppeal = useCallback(async () => {
    if (!selectedApplicationForAppeal) return;
    
    const reason = appealForm.reason?.trim() || '';
    const justification = appealForm.justification?.trim() || '';
    const questionNumber = appealForm.question_number;
    const errors = {};
    if (appealType === 'answer_key') {
      const qn = parseInt(questionNumber, 10);
      if (!Number.isFinite(qn) || qn < 1) {
        errors.question_number = 'Selecione a questão';
      } else {
        const proc = processes.find((p) => p.id === selectedApplicationForAppeal.process_id);
        const appealed = getAnswerKeyAppealedQuestionNumbers(selectedApplicationForAppeal);
        if (appealed.has(qn)) {
          errors.question_number = 'Você já enviou recurso para esta questão';
        }
        const total = totalExamQuestionsForProcess(proc);
        if (total > 0 && qn > total) {
          errors.question_number = `Questão deve ser entre 1 e ${total}`;
        }
      }
      if (!justification) {
        errors.justification = 'Descreva sua argumentação e o pedido';
      }
    } else {
      if (!reason) errors.reason = 'Motivo do recurso é obrigatório';
      if (!justification) errors.justification = 'Justificativa detalhada é obrigatória';
    }
    
    if (Object.keys(errors).length > 0) {
      setAppealFormErrors(errors);
      addNotification('Atenção', 'Preencha todos os campos obrigatórios', 'warning');
      return;
    }
    setAppealFormErrors({});

    try {
      setActionSubmitting(true);
      setAppealSubmitError('');
      // Criar o recurso
      const appeal = {
        id: Date.now(),
        application_id: selectedApplicationForAppeal.id,
        user_id: user.id,
        process_id: selectedApplicationForAppeal.process_id,
        type: 'exemption',
        reason,
        justification,
        documents: appealForm.documents,
        status: 'pending',
        created_at: new Date().toISOString()
      };

      // Salvar o recurso
      const payload = {
        application_id: selectedApplicationForAppeal.id,
        appeal_type: appealType,
        justification,
        documents: appealType === 'answer_key' ? [] : (appealForm.documents || []),
      };
      if (appealType === 'answer_key') {
        payload.question_number = parseInt(appealForm.question_number, 10);
      }
      const { error: appealError } = await apiClient.createAppeal(payload);
      if (appealError) throw new Error(appealError);
      // Só atualiza exemption_status para recurso de isenção (preliminary_result não altera a inscrição)
      if (appealType === 'exemption') {
        await apiClient.updateApplication(selectedApplicationForAppeal.id, {
          exemption_status: 'appealed'
        });
      }
      // special_attendance: backend marca SAR como appealed ao criar o recurso

      addNotification('✅ Sucesso', 'Recurso enviado com sucesso!', 'success');
      await loadData(true);
      setShowAppealModal(false);
      setSelectedApplicationForAppeal(null);
      setAppealForm({
        reason: '',
        documents: [],
        justification: '',
        question_number: ''
      });
      setAppealFormErrors({});
      setAppealSubmitError('');
    } catch (error) {
      console.error('❌ Erro ao salvar recurso:', error);
      const msg = friendlyNetworkError(error?.message || 'Erro ao enviar recurso. Tente novamente.');
      setAppealSubmitError(msg);
      addNotification('❌ Erro', msg, 'error');
    } finally {
      setActionSubmitting(false);
    }
  }, [selectedApplicationForAppeal, appealForm, appealType, user, addNotification, loadData, processes]);

  // Função para salvar novos documentos (reenvio curricular - espelho da isenção)
  const handleSaveNewDocuments = useCallback(async () => {
    if (documentFileUploading) {
      setDocumentUploadError('Aguarde o envio do arquivo terminar antes de confirmar.');
      return;
    }
    if (!selectedApplicationForUpload || newDocuments.length === 0) {
      setDocumentUploadError('Selecione pelo menos um documento antes de enviar.');
      addNotification('Atenção', 'Por favor, selecione pelo menos um documento', 'warning');
      return;
    }
    try {
      setActionSubmitting(true);
      setDocumentUploadError('');
      const docsForApi = newDocuments.map(d => ({ url: d.url, name: d.name || d.file?.name }));
      const { error } = await apiClient.resendCurricular(selectedApplicationForUpload.id, docsForApi);
      if (error) throw new Error(error);
      await loadData(true);
      addNotification('Sucesso', 'Documentos enviados com sucesso! Sua inscrição será reanalisada.', 'success');
      setShowDocumentUploadModal(false);
      setSelectedApplicationForUpload(null);
      setNewDocuments([]);
      setDocumentUploadError('');
    } catch (error) {
      const msg = friendlyNetworkError(error.message || 'Erro ao enviar documentos. Tente novamente.');
      setDocumentUploadError(msg);
      addNotification('Erro', msg, 'error');
    } finally {
      setActionSubmitting(false);
    }
  }, [selectedApplicationForUpload, newDocuments, addNotification, loadData, documentFileUploading]);

  const handleChangeQuota = useCallback((application) => {
    const proc = processes.find((p) => p.id === application.process_id);
    if (!proc || !isInRegistrationPeriod(proc)) {
      addNotification('Alteração de cota só é possível durante o período de inscrições.', 'warning');
      return;
    }
    if (proc.allow_change_quota_during_registration === false) {
      addNotification('Este processo não permite alterar a cota durante o período de inscrição.', 'warning');
      return;
    }
    setSelectedApplicationForQuotaChange(application);
    setQuotaChangeSelectedQuota(application.quota_type || 'AC');
    setShowQuotaChangeModal(true);
  }, [processes, isInRegistrationPeriod, addNotification]);

  const handleConfirmQuotaChange = useCallback(async () => {
    if (!selectedApplicationForQuotaChange || !quotaChangeSelectedQuota) return;
    const currentQuota = selectedApplicationForQuotaChange.quota_type || 'AC';
    if (quotaChangeSelectedQuota === currentQuota) {
      addNotification('A cota selecionada já é a atual.', 'warning');
      return;
    }
    try {
      setLoading(true);
      const { error } = await apiClient.updateApplicationQuota(selectedApplicationForQuotaChange.id, quotaChangeSelectedQuota);
      if (error) throw new Error(error);
      addNotification('Cota alterada com sucesso.', 'success');
      setShowQuotaChangeModal(false);
      setSelectedApplicationForQuotaChange(null);
      setQuotaChangeSelectedQuota('');
      await loadData(true);
    } catch (err) {
      addNotification(err.message || 'Erro ao alterar cota', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedApplicationForQuotaChange, quotaChangeSelectedQuota, addNotification, loadData]);

  // Função para trocar curso
  const handleChangeCourse = useCallback(async (application) => {
    console.log('🔄 handleChangeCourse chamado com:', application);
    console.log('📊 Estado atual:', { 
      processes: processes?.length || 0, 
      courses: courses?.length || 0,
      applications: applications?.length || 0
    });
    
    const process = processes.find(p => p.id === application.process_id);
    // Converter course_id para número para comparação
    const courseId = Number(application.course_id);
    const course = courses.find(c => c.id === courseId);
    
    console.log('🔍 Processo encontrado:', process);
    console.log('🔍 course_id original:', application.course_id, 'tipo:', typeof application.course_id);
    console.log('🔍 courseId convertido:', courseId, 'tipo:', typeof courseId);
    console.log('🔍 Curso encontrado:', course);
    console.log('🔍 Aplicação:', application);
    
    if (!process || !course) {
      console.error('❌ Processo ou curso não encontrado');
      addNotification('Erro: Processo ou curso não encontrado', 'error');
      return;
    }

    // Verificar se pode trocar de curso (período de inscrição ativo)
    const now = new Date();
    
    // 🔧 Função para converter data brasileira (DD/MM/YYYY) para Date object
    const convertBrazilianDateToDate = (brazilianDate) => {
      if (!brazilianDate) return null;
      
      // ISO (YYYY-MM-DD): usar new Date(y,m-1,d) para data LOCAL (evitar meia-noite UTC)
      if (brazilianDate.includes('-')) {
        const p = String(brazilianDate).trim().split('-');
        if (p.length >= 3) return new Date(parseInt(p[0],10), parseInt(p[1],10)-1, parseInt(p[2],10));
      }
      // Converte DD/MM/YYYY para Date object
      const parts = brazilianDate.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        // month - 1 porque Date() espera mês de 0-11
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      
      return null;
    };
    
    const startDate = convertBrazilianDateToDate(process.start_date);
    const endDate = convertBrazilianDateToDate(process.end_date);
    if (endDate) endDate.setHours(23, 59, 59, 999); // último dia inclui o dia inteiro
    const isInPeriod = startDate && endDate && now >= startDate && now <= endDate;
    
    console.log("📅 Período de inscrição:", { 
      startDate: startDate ? startDate.toISOString() : 'Data inválida', 
      endDate: endDate ? endDate.toISOString() : 'Data inválida', 
      now: now.toISOString(), 
      isInPeriod,
      startDateObj: startDate,
      endDateObj: endDate,
      nowObj: now
    });
    
    if (!isInPeriod) {
      console.log("❌ Não pode trocar curso - período de inscrição encerrado");
      addNotification("Não é possível trocar de curso fora do período de inscrição", "warning");
      return;
    }

    if (process.allow_change_course_during_registration === false) {
      addNotification('Este processo não permite alterar o curso durante o período de inscrição.', 'warning');
      return;
    }
    
    console.log("✅ Pode trocar curso - período ativo");
    
    // Removido: Verificação de pagamento - permitir troca independente do status
    console.log('✅ Troca de curso permitida - sem verificação de pagamento');
    console.log('✅ Configurando modal para troca de curso');
    
    // Configurar o modal para troca de curso
    console.log('🔍 ANTES de configurar formData - application.id:', application.id);
    console.log('🔍 ANTES de configurar formData - application:', application);
    
    const newFormData = {
      ...formData,
      isCourseChange: true,
      originalApplicationId: application.id,
      course_id: '', // Deixar vazio para usuário selecionar novo curso
      curricularDocuments: application.curricular_documents || [], // Manter documentos existentes
      // Troca de curso não reabre isenção no formulário — pagamento/isenoção permanecem na inscrição
      requestExemption: false,
      exemptionDocuments: [],
      exemptionNis: '',
      exemptionReason: '',
      requestSpecialAttendance: false,
      specialAttendanceTypeIds: [],
      specialAttendanceJustification: '',
      specialAttendanceDocuments: []
    };
    
    console.log('🔍 DEPOIS de configurar formData - newFormData:', newFormData);
    console.log('🔍 DEPOIS de configurar formData - originalApplicationId:', newFormData.originalApplicationId);
    
    console.log('🔍 ANTES de setFormData - newFormData:', newFormData);
    
    setFormData(newFormData);
    setSelectedProcess(process);
    setShowApplicationModal(true);
    
    console.log('✅ Modal configurado e aberto');
    addNotification('Modal de troca de curso aberto. Selecione o novo curso.', 'info');
    
    // Verificar se o estado foi atualizado corretamente
    setTimeout(() => {
      console.log('🔍 DEPOIS de setFormData - formData atual:', formData);
    }, 100);
  }, [processes, courses, formData, getPaymentStatus, addNotification]);

  // Função para gerar GRU (chama API e abre URL do PagTesouro)
  const generateGRU = useCallback(async (application) => {
    const process = processes.find(p => p.id === application?.process_id);
    if (!process) {
      addNotification('Erro', 'Processo não encontrado', 'error');
      return;
    }
    if (!process.has_fee) {
      addNotification('Aviso', 'Este processo não possui taxa de inscrição', 'info');
      return;
    }
    if (application.payment_status === 'paid') {
      addNotification('Aviso', 'Pagamento já foi confirmado. GRU não é necessária.', 'info');
      return;
    }
    const { data, error: err } = await apiClient.generateGruUrl(application.id);
    if (err) {
      addNotification('Erro', err, 'error');
      return;
    }
    if (data?.url) {
      window.open(data.url, '_blank', 'noopener,noreferrer');
      addNotification('Sucesso', `GRU gerada. Nº de referência: ${data.referencia || '—'}. Pague no Banco do Brasil.`, 'success');
    }
  }, [processes, addNotification]);

  const downloadEnrollmentCard = useCallback(async (application) => {
    try {
      const { blob, filename } = await apiClient.downloadEnrollmentCard(application.id);
      downloadBlobFile(blob, filename);
      addNotification('Sucesso', 'Cartão de inscrição gerado. Verifique o download ou impressão.', 'success');
    } catch (err) {
      addNotification('Erro', err.message || 'Não foi possível gerar o cartão de inscrição', 'error');
    }
  }, [addNotification]);

  const downloadRegistrationReceipt = useCallback(async (application) => {
    if (!application?.id || receiptDownloadingId) return;
    setReceiptDownloadingId(application.id);
    try {
      const { blob, filename } = await apiClient.downloadRegistrationReceipt(application.id);
      downloadBlobFile(blob, filename);
      addNotification('Sucesso', 'Comprovante gerado. Você pode abrir o PDF para imprimir ou salvar.', 'success');
    } catch (err) {
      addNotification('Erro', err.message || 'Não foi possível gerar o comprovante de inscrição', 'error');
    } finally {
      setReceiptDownloadingId(null);
    }
  }, [addNotification, receiptDownloadingId]);

  // Debug dos dados
  console.log('📊 Dados carregados:', { 
    processes: processes?.length || 0, 
    applications: applications?.length || 0, 
    courses: courses?.length || 0 
  });
  console.log('📋 Processos:', processes);
  console.log('📝 Inscrições:', applications);
  console.log('🎓 Cursos:', courses);
  
  // 🔍 DEBUG EXTRA: Verificar formato das datas
  if (processes && processes.length > 0) {
    console.log('🔍 DEBUG DATAS - Primeiro processo:');
    const firstProcess = processes[0];
    console.log('  📅 start_date:', firstProcess.start_date, 'tipo:', typeof firstProcess.start_date);
    console.log('  📅 end_date:', firstProcess.end_date, 'tipo:', typeof firstProcess.end_date);
    console.log('  📅 is_active:', firstProcess.is_active, 'tipo:', typeof firstProcess.is_active);
    
    // Testar conversão de data brasileira
    const convertBrazilianDateToDate = (brazilianDate) => {
      if (!brazilianDate) return null;
      if (brazilianDate.includes('-')) {
        const p = String(brazilianDate).trim().split('-');
        if (p.length >= 3) return new Date(parseInt(p[0],10), parseInt(p[1],10)-1, parseInt(p[2],10));
      }
      const parts = brazilianDate.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      return null;
    };
    
    const testStartDate = convertBrazilianDateToDate(firstProcess.start_date);
    const testEndDate = convertBrazilianDateToDate(firstProcess.end_date);
    console.log('  📅 start_date convertido:', testStartDate);
    console.log('  📅 end_date convertido:', testEndDate);
    console.log('  📅 start_date válida:', testStartDate && !isNaN(testStartDate.getTime()));
    console.log('  📅 end_date válida:', testEndDate && !isNaN(testEndDate.getTime()));
  }
  
  // 🔍 DEBUG: Verificar todos os processos carregados
  console.log('🔍 DEBUG - Todos os processos carregados:', processes.map(p => ({
    id: p.id,
    name: p.name,
    is_active: p.is_active,
    start_date: p.start_date,
    end_date: p.end_date
  })));
  
  // 🔍 DEBUG: Verificar especificamente o processo "Teste persistente"
  const testProcess = processes.find(p => p.name === 'Teste persistente');
  console.log('🔍 Processo "Teste persistente" encontrado:', testProcess);

  // Processos ativos: inscrições abertas ou em breve (antes do início do período)
  const availableProcesses = processes
    .filter((process) => process.is_active === true)
    .map((process) => ({
      ...process,
      registrationStatus: getProcessRegistrationStatus(process),
    }))
    .filter(
      (process) => process.registrationStatus === 'open' || process.registrationStatus === 'upcoming'
    )
    .sort((a, b) => {
      const statusOrder = { open: 0, upcoming: 1 };
      const byStatus = statusOrder[a.registrationStatus] - statusOrder[b.registrationStatus];
      if (byStatus !== 0) return byStatus;
      const startA = parseDateAsLocal(a.start_date)?.getTime() ?? 0;
      const startB = parseDateAsLocal(b.start_date)?.getTime() ?? 0;
      return startA - startB;
    });

  const parseScheduleUpdatedMs = (iso) => {
    if (!iso) return null;
    const s = String(iso).trim();
    const normalized = s.includes('T')
      ? (s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`)
      : `${s.replace(' ', 'T')}Z`;
    const t = Date.parse(normalized);
    return Number.isNaN(t) ? null : t;
  };
  const scheduleStampKey = (iso) => {
    if (!iso) return '';
    return String(iso).trim().replace(' ', 'T').slice(0, 19);
  };
  const isScheduleNoticePending = (procUpdatedAt, ackIso, enrolledAtIso) => {
    if (!procUpdatedAt) return false;
    const pMs = parseScheduleUpdatedMs(procUpdatedAt);
    if (pMs == null) return false;
    const eMs = parseScheduleUpdatedMs(enrolledAtIso);
    if (eMs != null && pMs <= eMs + 1000) return false;
    if (!ackIso) return true;
    if (scheduleStampKey(procUpdatedAt) === scheduleStampKey(ackIso)) return false;
    const aMs = parseScheduleUpdatedMs(ackIso);
    if (aMs == null) return scheduleStampKey(procUpdatedAt) !== scheduleStampKey(ackIso);
    return pMs > aMs + 1000;
  };
  const byProcessIdForSchedule = new Map(processes.map((p) => [String(p.id), p]));
  /** Só processos com inscrição ativa do candidato e cronograma ainda em andamento (não encerrado). */
  const enrolledActiveProcessIds = new Set();
  const enrolledAtByProcessId = new Map();
  applications.forEach((app) => {
    if (!app.excluded_at && app.process_id != null) {
      const pid = String(app.process_id);
      enrolledActiveProcessIds.add(pid);
      const prev = enrolledAtByProcessId.get(pid);
      const created = app.created_at;
      if (!prev || (created && new Date(created) > new Date(prev))) {
        enrolledAtByProcessId.set(pid, created);
      }
    }
  });
  const getScheduleItemsForProcess = (proc) => {
    if (!proc) return [];
    return processScheduleMap[proc.id] ?? processScheduleMap[String(proc.id)] ?? [];
  };
  const pendingScheduleNotice = [];
  enrolledActiveProcessIds.forEach((pid) => {
    const proc = byProcessIdForSchedule.get(pid);
    if (!proc?.schedule_updated_at) return;
    const scheduleItems = getScheduleItemsForProcess(proc);
    if (scheduleItems.length > 0 && isProcessScheduleFullyClosed(scheduleItems)) return;
    const ackIso = scheduleAckMap[pid] || null;
    const enrolledAt = enrolledAtByProcessId.get(pid);
    if (isScheduleNoticePending(proc.schedule_updated_at, ackIso, enrolledAt)) {
      pendingScheduleNotice.push({ id: proc.id, name: proc.name });
    }
  });

  // 🔍 DEBUG: Mostrar processos disponíveis finais
  console.log('🔍 PROCESSOS DISPONÍVEIS FINAIS:', availableProcesses.map(p => ({
    id: p.id,
    name: p.name,
    is_active: p.is_active,
    registrationStatus: p.registrationStatus,
  })));

  // Fallback para debug
  if (!user) {
    console.log('❌ Usuário não encontrado');
    return <div style={{ padding: '20px', color: 'red' }}>Erro: Usuário não encontrado</div>;
  }

  if (loading && !hasModalOpen && applications.length === 0) {
    return <LoadingSpinner text="Carregando seus dados..." />;
  }

  // Componente de Timeline de Status - 2 selos compactos
  const badgeStyle = (step) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    backgroundColor: step.bg || (step.status === 'completed' ? '#F0FDF4' : step.status === 'rejected' ? '#FEF2F2' : '#FFFBEB'),
    borderRadius: '6px',
    border: `1px solid ${step.color}`,
    borderStyle: step.status === 'completed' ? 'solid' : 'dashed',
    listStyle: 'none'
  });
  const StatusTimeline = ({ application }) => {
    const status = getDetailedApplicationStatus(application);
    const timelineSteps = [];
    if (status.payment) {
      timelineSteps.push({ key: 'payment', step: status.payment });
    }
    if (status.isProva && status.provaEnrollment) {
      if (status.provaAttendance) {
        timelineSteps.push({ key: 'provaAttendance', step: status.provaAttendance });
      }
      timelineSteps.push({ key: 'provaEnrollment', step: status.provaEnrollment });
    } else if (status.analysis) {
      timelineSteps.push({ key: 'analysis', step: status.analysis });
    }

    const titleColor = (step) => {
      if (step.status === 'completed') return '#065F46';
      if (step.status === 'rejected') return '#991B1B';
      if (step.status === 'pending' && step.color === '#6366F1') return '#3730A3';
      if (step.status === 'pending' && step.color === '#6B7280') return '#374151';
      if (step.status === 'pending' && step.color === '#F59E0B') return '#92400E';
      return '#92400E';
    };

    return (
      <div data-status-timeline style={{ marginTop: '8px', listStyle: 'none', paddingLeft: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', listStyle: 'none' }}>
          {timelineSteps.map(({ key, step }, idx) => (
            <div key={key} style={badgeStyle(step)}>
              <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: step.color,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                {step.status === 'completed' ? '✓' : String(idx + 1)}
              </div>
              <div style={{ flex: 1, minWidth: 0, listStyle: 'none', display: 'block' }}>
                <div style={{ fontWeight: '600', color: titleColor(step), fontSize: '12px', listStyle: 'none', display: 'block' }}>
                  {step.icon} {step.label}
                </div>
                <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '1px', listStyle: 'none', display: 'block' }}>
                  {step.description}
                  {step.mediaFinal && (
                    <span style={{ fontWeight: '700', fontSize: '13px', color: '#065F46', marginLeft: '4px' }}>
                      Média final: {step.mediaFinal}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.contentContainer} className="ingressa-content-container candidate-area-root">
      <div style={styles.card} className="candidate-area-card">
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle} className="candidate-area-title">👤 Área do Candidato</h2>
        </div>

        <CandidatePwaUpdateBanner />
        <CandidatePwaInstallBanner />
        <CandidatePushOptInBanner />

        {pendingScheduleNotice.length > 0 && !showApplicationModal && (
          <div
            style={{
              ...styles.alert,
              ...styles.alertInfo,
              marginBottom: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: '12px',
            }}
          >
            <div style={{ fontSize: '14px', color: '#1e3a5f' }}>
              <strong>Houve alteração do cronograma.</strong>
              {pendingScheduleNotice.length === 1 ? (
                <span> Confira o cronograma do processo: {pendingScheduleNotice[0].name}.</span>
              ) : (
                <span>
                  {' '}
                  Confira o cronograma nos processos:{' '}
                  {pendingScheduleNotice.map((p) => p.name).join(', ')}.
                </span>
              )}
            </div>
            <div>
              <button
                type="button"
                disabled={scheduleAckBusy}
                onClick={() => acknowledgeScheduleNotice(pendingScheduleNotice.map((p) => p.id))}
                style={{
                  ...styles.button,
                  ...styles.buttonPrimary,
                  opacity: scheduleAckBusy ? 0.7 : 1,
                  cursor: scheduleAckBusy ? 'wait' : 'pointer',
                  alignSelf: 'flex-start',
                }}
              >
                Entendi
              </button>
            </div>
          </div>
        )}

        {/* Processos Disponíveis */}
        {availableProcesses.length > 0 ? (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '8px', color: '#374151' }}>🎯 Processos Disponíveis</h3>
            <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px', lineHeight: 1.5 }}>
              Processos ativos com inscrições abertas ou previstas. Você pode consultar editais e cronograma antes da abertura.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }} className="candidate-process-grid">
              {availableProcesses.map(process => {
                const isUpcoming = process.registrationStatus === 'upcoming';
                return (
                <div key={process.id} style={{ 
                  padding: '20px', 
                  border: `1px solid ${isUpcoming ? '#FCD34D' : '#E5E7EB'}`, 
                  borderRadius: '12px',
                  backgroundColor: isUpcoming ? '#FFFBEB' : '#FFFFFF'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '8px' }}>
                    <h4 style={{ margin: 0, color: '#1F2937', fontWeight: '600' }}>
                      {process.name}
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'flex-end' }}>
                      {isUpcoming && (
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: '#FEF3C7',
                          color: '#92400E',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                        }}>
                          Em breve
                        </span>
                      )}
                      <span style={{ 
                        padding: '4px 8px', 
                        backgroundColor: isUpcoming ? '#D97706' : '#10B981', 
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {process.code}
                      </span>
                    </div>
                  </div>
                  
                  <p style={{ color: '#6B7280', marginBottom: '16px', fontSize: '14px' }}>
                    {process.description}
                  </p>

                  {isUpcoming && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: '#FEF3C7',
                      color: '#92400E',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      fontSize: '14px',
                      lineHeight: 1.5,
                      border: '1px solid #FDE68A',
                    }}>
                      As inscrições começam em <strong>{formatDate(process.start_date)}</strong>.
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                      <div>📅 {formatDate(process.start_date)} - {formatDate(process.end_date)}</div>
                      <div>💰 {process.has_fee ? formatCurrency(process.fee_amount) : 'Gratuito'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setDocsModalProcess(process)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#3B82F6',
                        fontSize: '13px',
                        cursor: 'pointer',
                        padding: '4px 0',
                        textAlign: 'left'
                      }}
                    >
                      📄 Editais e anexos
                    </button>
                    <button
                      type="button"
                      onClick={() => setScheduleModalProcess(process)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#3B82F6',
                        fontSize: '13px',
                        cursor: 'pointer',
                        padding: '4px 0',
                        textAlign: 'left'
                      }}
                    >
                      📅 Cronograma
                    </button>
                  </div>
                  {(() => {
                    // Verificar se já tem inscrição neste processo
                    const hasApplication = applications.some(app => {
                      // Converter course_id para número para comparação
                      const courseId = Number(app.course_id);
                      const appCourse = courses.find(c => c.id === courseId);
                      return appCourse?.process_id === process.id && app.user_id === user.id;
                    });
                    
                    
                    // Se já tem inscrição
                    if (hasApplication) {
                      const userApplication = applications.find(app => {
                        // Converter course_id para número para comparação
                        const courseId = Number(app.course_id);
                        const appCourse = courses.find(c => c.id === courseId);
                        return appCourse?.process_id === process.id && app.user_id === user.id;
                      });
                      
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{
                            padding: '12px',
                            backgroundColor: '#D1FAE5',
                            color: '#065F46',
                            borderRadius: '8px',
                            textAlign: 'center',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            ✅ Inscrição realizada
                          </div>
                          
                        </div>
                      );
                    }
                    
                    if (isUpcoming) {
                      return (
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#F3F4F6',
                          color: '#4B5563',
                          borderRadius: '8px',
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '500',
                        }}>
                          Inscrições ainda não abertas
                        </div>
                      );
                    }

                    return (
                      <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                        <button
                          onClick={() => handleNewApplication(process)}
                          style={{
                            ...styles.button,
                            ...styles.buttonPrimary,
                            width: '100%'
                          }}
                        >
                          📝 Inscrever-se
                        </button>
                      </div>
                    );
                  })()}
                </div>
              );
              })}
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '32px' }}>
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
              <h4>Nenhum processo seletivo no momento</h4>
              <p>Não há processos ativos com inscrições abertas ou previstas</p>
            </div>
          </div>
        )}

        {/* Minhas Inscrições */}
        <div>
          <h3 style={{ marginBottom: '16px', color: '#374151' }}>📋 Minhas Inscrições</h3>
          
          {applications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
              <h4>Nenhuma inscrição encontrada</h4>
              <p>Você ainda não se inscreveu em nenhum processo seletivo</p>
            </div>
          ) : (() => {
              const groupedByProcess = {};
              applications.forEach(app => {
                const pid = app.process_id;
                if (!groupedByProcess[pid]) groupedByProcess[pid] = [];
                groupedByProcess[pid].push(app);
              });
              const processIds = Object.keys(groupedByProcess).map(Number);
              const showExpandCollapse = processIds.length > 1;

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {processIds.map(processId => {
                    const processApps = groupedByProcess[processId];
                    const process = processes.find(p => p.id === processId);
                    const isCollapsed = collapsedProcessIds.has(processId);
                    const activeApps = processApps.filter(a => !a.excluded_at);
                    const activeCount = activeApps.length;

                    const summaryText = `${activeCount} inscrição${activeCount !== 1 ? 'ões' : ''}`;
                    const scheduleForProcess = process ? processScheduleMap[process.id] : undefined;
                    const showEncerradoSeal = process && isProcessScheduleFullyClosed(scheduleForProcess);

                    return (
                      <div key={processId} style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
                        <div
                          onClick={showExpandCollapse ? () => toggleProcessExpand(processId) : undefined}
                          style={{
                            padding: '12px 16px',
                            backgroundColor: '#F9FAFB',
                            cursor: showExpandCollapse ? 'pointer' : 'default',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: (!showExpandCollapse || !isCollapsed) ? '1px solid #E5E7EB' : 'none'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                            {showExpandCollapse && <span style={{ fontSize: '18px' }}>{isCollapsed ? '▶' : '▼'}</span>}
                            <div>
                              <div style={{ fontWeight: '600', color: '#1E40AF', fontSize: '15px' }}>{process?.name || 'Processo'}</div>
                              {showEncerradoSeal ? (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    marginTop: '5px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    color: '#C2410C',
                                    backgroundColor: '#FFEDD5',
                                    border: '1px solid #EA580C',
                                    borderRadius: '4px',
                                    padding: '2px 8px',
                                    lineHeight: 1.35,
                                  }}
                                >
                                  Encerrado
                                </span>
                              ) : (
                                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{summaryText}</div>
                              )}
                            </div>
                            {process && (
                              <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setDocsModalProcess(process); }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#3B82F6',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    padding: '4px 8px'
                                  }}
                                >
                                  📄 Editais
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setScheduleModalProcess(process); }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#3B82F6',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    padding: '4px 8px'
                                  }}
                                >
                                  📅 Cronograma
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        {(!showExpandCollapse || !isCollapsed) && (
                          <>
                            {process && processScheduleMap[process.id]?.filter(i => i.status === 'aberto').length > 0 && (
                              <div style={{
                                margin: '12px 16px',
                                padding: '10px 12px',
                                backgroundColor: '#D1FAE5',
                                border: '1px solid #6EE7B7',
                                borderRadius: '8px',
                                fontSize: '13px',
                                color: '#065F46'
                              }}>
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>⚠️ Etapa em aberto</div>
                                {processScheduleMap[process.id]
                                  .filter(i => i.status === 'aberto')
                                  .map((item, idx) => (
                                    <div key={idx}>{item.label}</div>
                                  ))}
                              </div>
                            )}
                          <div className="candidate-apps-table-wrap" style={{ overflowX: 'auto' }}>
                            <table style={styles.table} className="candidate-apps-table">
                              <thead style={styles.tableHeader}>
                                <tr>
                                  <th style={{ ...styles.tableHeaderCell, width: '50px' }}>Nº</th>
                                  <th style={styles.tableHeaderCell}>Curso</th>
                                  {process?.use_quotas && <th style={styles.tableHeaderCell}>Cota</th>}
                                  <th style={styles.tableHeaderCell}>Data da Inscrição</th>
                                  <th style={styles.tableHeaderCell}>Situação da Inscrição</th>
                                  <th style={styles.tableHeaderCell}>Ações</th>
                                </tr>
                              </thead>
                              <tbody>
                                {processApps.map(application => {
                    // Converter course_id para número para comparação
                    const courseId = Number(application.course_id);
                    const course = courses.find(c => c.id === courseId);
                    const process = processes.find(p => p.id === application.process_id);
                    const courseByProcess = courses.find(c => c.process_id === application.process_id && c.id === courseId);
                    const finalCourse = course || courseByProcess;
                    const status = getApplicationStatus(application);
                    const paymentStatus = getPaymentStatus(process?.id);
                    const paymentAmount = getPaymentAmount(process?.id);
                    const isExcluded = !!application.excluded_at;
                    const compactStyle = isExcluded ? { fontSize: '11px' } : {};
                    return (
                      <tr key={application.id} style={{ ...styles.tableRow, ...compactStyle }}>
                        <td data-label="Nº" style={{ ...styles.tableCell, ...compactStyle, fontWeight: '600', color: '#6B7280', fontSize: '12px' }}>
                          #{application.id}
                        </td>
                        <td data-label="Curso" style={{ ...styles.tableCell, ...compactStyle }}>
                          <div style={{ fontWeight: '500', color: '#1F2937' }}>
                            {formatCourseDisplayFromObject(
                              finalCourse || {
                                course_name: application.course_name,
                                course_campus: application.course_campus,
                              },
                            )}
                          </div>
                        </td>
                        {process?.use_quotas && (
                        <td data-label="Cota" style={{ ...styles.tableCell, ...compactStyle }}>{application.quota_type || 'AC'}</td>
                        )}
                        <td data-label="Data da inscrição" style={{ ...styles.tableCell, ...compactStyle }}>
                          {formatDate(application.created_at)}
                        </td>

                        <td data-label="Situação" style={{ ...styles.tableCell, listStyle: 'none', ...compactStyle }}>
                          {isExcluded ? (
                            <span style={{
                              display: 'inline-block',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: '#991B1B',
                              backgroundColor: '#FEE2E2',
                              border: '1px solid #FCA5A5'
                            }}>
                              Excluída
                            </span>
                          ) : (
                            <>
                              <StatusTimeline application={application} />
                            </>
                          )}
                        </td>
                        <td data-label="Ações" className="candidate-apps-actions" style={{ ...styles.tableCell, ...compactStyle }}>
                          {isExcluded ? (
                            <span style={{ color: '#9CA3AF', fontSize: '11px' }}>—</span>
                          ) : (
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {/* Botão Gerar GRU - usa status da própria inscrição (evita bug após troca de curso) */}
                            {process?.has_fee && (() => {
                              const paymentConfirmed = application.exemption_status === 'approved' || application.exemption_status === 'appeal_approved' || application.payment_status === 'paid' || application.payment_status === 'exempted';
                              return !paymentConfirmed && application.exemption_status !== 'pending';
                            })() && (
                              <button
                                onClick={() => generateGRU(application)}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonWarning,
                                  ...styles.buttonSmall
                                }}
                                title="Gerar GRU para pagamento"
                              >
                                💰 Gerar GRU
                              </button>
                            )}
                            
                            {process?.selection_mode === 'prova' && application.exam_enrollment_card_available && (
                              <button
                                type="button"
                                onClick={() => downloadEnrollmentCard(application)}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonPrimary,
                                  ...styles.buttonSmall
                                }}
                                title="Imprimir cartão de inscrição definitivo (local e horários da prova)"
                              >
                                🪪 Cartão de inscrição
                              </button>
                            )}

                            {isCurricularLikeMode(process?.selection_mode) && application.registration_receipt_available !== false && !isExcluded && (
                              <button
                                type="button"
                                onClick={() => downloadRegistrationReceipt(application)}
                                disabled={receiptDownloadingId === application.id}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonSecondary,
                                  ...styles.buttonSmall,
                                  opacity: receiptDownloadingId === application.id ? 0.7 : 1,
                                  cursor: receiptDownloadingId === application.id ? 'wait' : 'pointer',
                                }}
                                title="Imprimir ou salvar comprovante de inscrição (PDF)"
                              >
                                {receiptDownloadingId === application.id ? '⏳ Gerando…' : '📄 Comprovante'}
                              </button>
                            )}

                            {/* Botão Trocar Curso - apenas durante período de inscrições */}
                            {!isExcluded && isInRegistrationPeriod(process) && process.allow_change_course_during_registration !== false && (
                              <button
                                onClick={() => handleChangeCourse(application)}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonSecondary,
                                  ...styles.buttonSmall
                                }}
                                title="Trocar para outro curso"
                              >
                                🔄 Trocar Curso
                              </button>
                            )}

                            {/* Botão Alterar cota - durante período de inscrições, processo com cotas */}
                            {(() => {
                              const proc = processes.find(p => p.id === application.process_id);
                              const courseId = application.course_id;
                              const crs = courses.find(c => c.id === courseId);
                              const qv = crs?.quota_vacancies || [];
                              const hasMultipleQuotas = qv.length > 1;
                              const currentQ = application.quota_type || 'AC';
                              const hasOtherOption = qv.some(item => {
                                const q = quotas.find(qu => qu.id === item.quota_id);
                                return q && q.code !== currentQ;
                              });
                              return !isExcluded && proc?.use_quotas && proc?.allow_change_quota_during_registration !== false && isInRegistrationPeriod(proc) && (hasMultipleQuotas || hasOtherOption);
                            })() && (
                              <button
                                onClick={() => handleChangeQuota(application)}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonSecondary,
                                  ...styles.buttonSmall
                                }}
                                title="Alterar apenas a cota da inscrição"
                              >
                                📋 Alterar cota
                              </button>
                            )}
                            
                            {/* Botão Reenviar pedido de isenção - indeferido, durante período, pagamento NÃO confirmado */}
                            {(() => {
                              const isRejected = application.exemption_status === 'rejected';
                              const paymentConfirmed = paymentStatus === 'paid' || application.payment_status === 'paid';
                              const isInExemptionPeriodCheck = isInExemptionPeriod(process);
                              const isInAppealPeriodCheck = isInAppealPeriod(process);
                              const resendExemptionAllowed = process?.allow_resend_exemption_in_exemption_period !== false;
                              const shouldShow = isRejected && !paymentConfirmed && isInExemptionPeriodCheck && !isInAppealPeriodCheck && resendExemptionAllowed;
                              return shouldShow;
                            })() && (
                              <button
                                onClick={() => handleResendExemption(application)}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonWarning,
                                  ...styles.buttonSmall
                                }}
                                title="Reenviar pedido de isenção com novo documento"
                              >
                                📄 Reenviar pedido de isenção
                              </button>
                            )}

                            {/* Reenvio atendimento especial indeferido — período de pedido, fora do período de recurso AT esp */}
                            {(() => {
                              if (process?.selection_mode !== 'prova') return false;
                              if (!processOffersSpecialAttendance(process)) return false;
                              if (!application.special_attendance_request_id) return false;
                              const st = String(application.special_attendance_request_status || '').toLowerCase();
                              if (st !== 'rejected') return false;
                              if (!isInSpecialAttendanceRequestPeriod(process)) return false;
                              if (isInSpecialAttendanceAppealScheduleWindow(process)) return false;
                              if (process?.allow_resend_special_attendance_in_request_period === false) return false;
                              return true;
                            })() && (
                              <button
                                onClick={() => handleResendSpecialAttendance(application)}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonWarning,
                                  ...styles.buttonSmall
                                }}
                                title="Reenviar documentos do atendimento especial para nova análise"
                              >
                                📄 Reenviar atendimento especial
                              </button>
                            )}
                            
                            {/* Botão Enviar Novo Documento - análise curricular reprovada, APENAS durante período de inscrições */}
                            {application.analysis_status === 'rejected' && isInRegistrationPeriod(process) && process?.selection_mode !== 'prova' && !isIntentionLetterMode(process?.selection_mode) && process?.allow_resend_curricular_in_registration_period !== false && (
                              <button
                                onClick={() => handleUploadNewDocument(application)}
                                style={{ ...styles.button, ...styles.buttonWarning, ...styles.buttonSmall }}
                                title="Enviar novo documento curricular para reanálise"
                              >
                                📄 Enviar Novo Documento
                              </button>
                            )}
                            
                            {/* Botão Recurso Isenção - durante período de recurso de isenção, só para indeferidos */}
                            {(() => {
                              const isRejected = application.exemption_status === 'rejected';
                              const isInAppealPeriodCheck = isInAppealPeriod(process);
                              return isRejected && isInAppealPeriodCheck;
                            })() && (
                              <button
                                onClick={() => handleAppeal(application, 'exemption')}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonDanger,
                                  ...styles.buttonSmall
                                }}
                                title="Solicitar recurso contra indeferimento da isenção"
                              >
                                📝 Solicitar Recurso
                              </button>
                            )}
                            
                            {/* Botão Recurso Lista - durante período de recurso lista, para TODOS os inscritos (período inscrições encerrado) */}
                            {(() => {
                              const enrollmentEnded = !isInRegistrationPeriod(process);
                              const isInListAppealCheck = isInListAppealPeriod(process);
                              const hasListAppeal = application.list_appeal != null;
                              const notExcluded = !application.excluded_at;
                              return enrollmentEnded && isInListAppealCheck && !hasListAppeal && notExcluded;
                            })() && (
                              <button
                                onClick={() => handleAppeal(application, 'list')}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonDanger,
                                  ...styles.buttonSmall
                                }}
                                title="Solicitar recurso contra a Lista de inscritos"
                              >
                                📝 Solicitar Recurso
                              </button>
                            )}

                            {/* Botão Recurso Resultado Preliminar */}
                            {(() => {
                              const isInResultAppealCheck = isInResultAppealPeriod(process);
                              const hasResultAppeal = application.result_appeal != null;
                              const notExcluded = !application.excluded_at;
                              const isProva = process?.selection_mode === 'prova';
                              const canResultAppeal = isProva
                                ? application.exam_final_score != null && isProvaEnrollmentConfirmed(application)
                                : application.analysis_status === 'approved';
                              return isInResultAppealCheck && !hasResultAppeal && canResultAppeal && notExcluded;
                            })() && (
                              <button
                                onClick={() => handleAppeal(application, 'preliminary_result')}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonDanger,
                                  ...styles.buttonSmall
                                }}
                                title="Solicitar recurso contra o Resultado preliminar"
                              >
                                📝 Solicitar Recurso
                              </button>
                            )}

                            {(() => {
                              if (process?.selection_mode !== 'prova') return false;
                              if (!process?.exam_uses_answer_sheet) return false;
                              if (!isInAnswerKeyAppealPeriod(process)) return false;
                              if (!canSubmitMoreAnswerKeyAppeals(application, process)) return false;
                              if (application.excluded_at) return false;
                              return isProvaEnrollmentConfirmed(application);
                            })() && (
                              <button
                                onClick={() => handleAppeal(application, 'answer_key')}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonDanger,
                                  ...styles.buttonSmall
                                }}
                                title="Solicitar recurso contra o gabarito (uma vez por questão)"
                              >
                                📝 Recurso Gabarito
                                {getAnswerKeyAppealsList(application).length > 0
                                  ? ` (${getAnswerKeyAppealsList(application).length} enviado(s))`
                                  : ''}
                              </button>
                            )}

                            {(() => {
                              if (process?.selection_mode !== 'prova') return false;
                              if (!processOffersSpecialAttendance(process)) return false;
                              if (!isInSpecialAttendanceAppealScheduleWindow(process)) return false;
                              if (!application.special_attendance_request_id) return false;
                              const st = String(application.special_attendance_request_status || '').toLowerCase();
                              if (st !== 'rejected') return false;
                              if (application.special_attendance_appeal != null) return false;
                              return true;
                            })() && (
                              <button
                                onClick={() => handleAppeal(application, 'special_attendance')}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonDanger,
                                  ...styles.buttonSmall
                                }}
                                title="Solicitar recurso contra indeferimento do atendimento especial"
                              >
                                📝 Solicitar Recurso (atendimento especial)
                              </button>
                            )}
                            
                            {/* Botão Ver recurso - quando já enviou recurso (isenção, lista ou resultado preliminar) */}
                            {((['appealed', 'appeal_approved', 'appeal_rejected'].includes(application.exemption_status) && application.appeal) || application.list_appeal || application.result_appeal || application.special_attendance_appeal || getAnswerKeyAppealsList(application).length > 0) && (
                              <button
                                onClick={() => handleViewAppeal(application)}
                                style={{
                                  ...styles.button,
                                  ...styles.buttonPrimary,
                                  ...styles.buttonSmall
                                }}
                                title="Ver, salvar ou imprimir seu recurso"
                              >
                                {ACTION_ICONS.view} Ver Recurso
                              </button>
                            )}
                          </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                                </tbody>
</table>
                          </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })() }
        </div>


        {/* Histórico de Alterações */}
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ marginBottom: '10px', color: '#374151', fontSize: '16px' }}>📋 Histórico de Alterações</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead style={styles.tableHeader}>
                  <tr>
                    <th style={{ ...styles.tableHeaderCell, padding: '8px 10px', fontSize: '13px' }}>Tipo</th>
                    <th style={{ ...styles.tableHeaderCell, padding: '8px 10px', fontSize: '13px' }}>Processo</th>
                    <th style={{ ...styles.tableHeaderCell, padding: '8px 10px', fontSize: '13px' }}>Descrição</th>
                    <th style={{ ...styles.tableHeaderCell, padding: '8px 10px', fontSize: '13px' }}>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const courseItems = courseChanges.map(change => ({
                      ...change,
                      type: 'course_change',
                      typeLabel: 'Troca de Curso',
                      typeIcon: '🔄',
                      description: formatCourseChangeHistoryDescription(change, courses),
                      date: change.changed_at || change.created_at
                    }));
                    const quotaItems = quotaChanges.map(change => ({
                      ...change,
                      type: 'quota_change',
                      typeLabel: 'Alteração de Cota',
                      typeIcon: '📋',
                      description: `Cota alterada de ${change.from_quota_type || 'AC'} para ${change.to_quota_type || 'AC'}`,
                      date: change.changed_at || change.created_at
                    }));
                    const allChanges = [...courseItems, ...quotaItems].sort((a, b) => new Date(b.date) - new Date(a.date));

                    if (allChanges.length === 0) {
                      return (
                        <tr>
                          <td colSpan="4" style={{ textAlign: 'center', padding: '24px 16px', color: '#6B7280', fontSize: '13px' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📋</div>
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>Nenhuma alteração registrada</div>
                            <div>Você ainda não realizou alterações no sistema</div>
                          </td>
                        </tr>
                      );
                    }

                    const compactCell = { ...styles.tableCell, padding: '6px 10px', fontSize: '13px' };
                    return allChanges.map(change => {
                      const process = processes.find(p => p.id === change.process_id);
                      
                      return (
                        <tr key={change.id} style={styles.tableRow}>
                          <td style={compactCell}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontSize: '14px' }}>{change.typeIcon}</span>
                              <span style={{ fontWeight: '600', color: '#1F2937', fontSize: '13px' }}>
                                {change.typeLabel}
                              </span>
                            </div>
                          </td>
                          <td style={compactCell}>
                            <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '13px' }}>
                              {process?.name || 'Processo não encontrado'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '1px' }}>
                              {process?.code || 'Código não disponível'}
                            </div>
                          </td>
                          <td style={compactCell}>
                            <div style={{ color: '#6B7280', fontSize: '13px', lineHeight: 1.3 }}>
                              {change.description}
                            </div>
                          </td>
                          <td style={compactCell}>
                            {formatDate(change.date)}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
      </div>

      {/* Modal de Nova Inscrição */}
      <Modal
        isOpen={showApplicationModal}
        onClose={() => { setShowApplicationModal(false); setMatrixCheckResult(null); }}
        title={formData.isCourseChange ? `Trocar Curso - ${selectedProcess?.name}` : `Nova Inscrição - ${selectedProcess?.name}`}
      >
        <form onSubmit={handleSubmitApplication} style={styles.form}>
          {error && (
            <div
              ref={formTopRef}
              style={{
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: '#FEF2F2',
              border: '1px solid #EF4444',
              borderRadius: '8px',
              color: '#B91C1C',
              fontSize: '14px'
            }}
            >
              ⚠️ {error}
            </div>
          )}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Curso *</label>
              <select
                value={formData.course_id}
                onChange={(e) => handleInputChange('course_id', e.target.value)}
                style={styles.select}
                required
              >
                <option value="">Selecione um curso...</option>
                {courses
                  .filter(course => course.process_id === selectedProcess?.id)
                  .map(course => (
                    <option key={course.id} value={course.id}>
                      {formatCourseDisplayFromObject(course)}
                    </option>
                  ))}
              </select>
              {courses.filter(course => course.process_id === selectedProcess?.id).length === 0 && (
                <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                  Nenhum curso encontrado para este processo
                </p>
              )}
              {formData.isCourseChange && matrixCheckResult && (
                <p style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: matrixCheckResult.same_matrix ? '#D1FAE5' : '#FEF3C7',
                  color: matrixCheckResult.same_matrix ? '#065F46' : '#92400E',
                  border: `1px solid ${matrixCheckResult.same_matrix ? '#10B981' : '#F59E0B'}`
                }}>
                  {isOrdemInscricaoMode(selectedProcess?.selection_mode)
                    ? 'Por ordem de inscrição: apenas troca de curso/unidade. Documentos e situação de pagamento/isenção são mantidos.'
                    : selectedProcess?.selection_mode === 'prova'
                    ? (matrixCheckResult.same_matrix
                      ? 'Mesma configuração de prova entre os cursos: apenas troca de curso/unidade.'
                      : 'Configuração de prova diferente: ao confirmar, o sistema excluirá a inscrição atual e criará uma nova; pagamento ou isenção serão preservados. Não é necessário enviar documentos curriculares.')
                    : (matrixCheckResult.same_matrix
                      ? 'Matriz igual: apenas troca de curso/unidade.'
                      : 'Matriz diferente: o sistema excluirá automaticamente sua inscrição atual ao clicar em "Confirmar Troca de Curso" e criará uma nova com os documentos curriculares do novo curso. A situação atual, de pagamento ou isenção, será preservada.')}
                </p>
              )}
            </div>
          </div>

          {!formData.isCourseChange && selectedProcess?.selection_mode === 'prova' && selectedProcess?.exam_allocation_policy === 'candidate_choice' && (
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Local de prova escolhido *</label>
                <select
                  value={formData.exam_preferred_venue_id}
                  onChange={(e) => handleInputChange('exam_preferred_venue_id', e.target.value)}
                  style={styles.select}
                  required
                >
                  <option value="">Selecione um local de prova...</option>
                  {examVenueOptions.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}{venue.city_state ? ` - ${venue.city_state}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {selectedProcess?.use_quotas === true && (
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Cota *</label>
              <select
                value={formData.quota_type}
                onChange={(e) => handleInputChange('quota_type', e.target.value)}
                style={styles.select}
                required
              >
                <option value="">Selecione uma cota...</option>
                {(() => {
                  const selectedCourse = courses.find(c => c.id === parseInt(formData.course_id, 10));
                  const qv = selectedCourse?.quota_vacancies;
                  if (!qv?.length) {
                    return <option value="AC">AC - Ampla Concorrência</option>;
                  }
                  return qv
                    .filter(item => (item.vacancies || 0) > 0)
                    .map(item => {
                      const quota = quotas.find(q => q.id === item.quota_id);
                      const code = quota?.code || 'AC';
                      const desc = quota?.description || 'Ampla Concorrência';
                      return (
                        <option key={item.quota_id} value={code}>
                          {code} - {desc} ({item.vacancies} vagas)
                        </option>
                      );
                    });
                })()}
              </select>
            </div>
          </div>
          )}

          {/* Questionário Socioeconômico */}
          {processQuestionnaire && !formData.isCourseChange && (
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%' }}>
                <div style={{ padding: '16px', border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#F9FAFB', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 8px 0', color: '#1F2937', fontSize: '15px' }}>📋 {processQuestionnaire.name}</h4>
                  {processQuestionnaire.intro_text && (
                    <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>{processQuestionnaire.intro_text}</p>
                  )}
                  {unansweredQuestionnaireItems.length > 0 && (
                    <div
                      role="status"
                      style={{
                        marginBottom: '16px',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #F59E0B',
                        backgroundColor: '#FFFBEB',
                        fontSize: '13px',
                        color: '#92400E',
                        lineHeight: 1.45,
                      }}
                    >
                      <strong>Falta responder {unansweredQuestionnaireItems.length} pergunta(s).</strong> Elas estão destacadas abaixo; escolha uma alternativa em cada uma para poder enviar a inscrição.
                    </div>
                  )}
                  {(processQuestionnaire.questions || []).map((q) => {
                    const isUnanswered =
                      unansweredQuestionnaireIdSet.has(q.id) || unansweredQuestionnaireIdSet.has(String(q.id));
                    return (
                      <div
                        key={q.id}
                        id={`questionnaire-q-${q.id}`}
                        style={{
                          marginBottom: '16px',
                          padding: isUnanswered ? '12px 14px' : '0',
                          borderRadius: '8px',
                          border: isUnanswered ? '2px solid #F59E0B' : '1px solid transparent',
                          backgroundColor: isUnanswered ? '#FFFBEB' : 'transparent',
                          scrollMarginTop: '24px',
                        }}
                      >
                        <label style={{ ...styles.label, display: 'block', marginBottom: '8px' }}>
                          {q.question_text} <span style={{ color: '#EF4444' }}>*</span>
                          {isUnanswered && (
                            <span
                              style={{
                                display: 'inline-block',
                                marginLeft: '8px',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#B45309',
                              }}
                            >
                              Sem resposta
                            </span>
                          )}
                        </label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {(q.alternatives || []).map((alt) => (
                            <label key={alt.id} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px' }}>
                              <input
                                type="radio"
                                name={`q_${q.id}`}
                                value={alt.id}
                                checked={String(formData.questionnaireAnswers?.[String(q.id)] ?? formData.questionnaireAnswers?.[q.id]) === String(alt.id)}
                                onChange={() => handleQuestionnaireAnswer(q.id, alt.id)}
                                style={{ marginRight: '8px' }}
                              />
                              {alt.alternative_text}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Carta de intenção */}
          {selectedProcess && isIntentionLetterMode(selectedProcess.selection_mode) && !formData.isCourseChange && (
            <div style={styles.formRow}>
              <div style={{ ...styles.formGroup, flex: '1 1 100%' }}>
                <label style={styles.label}>
                  Carta de intenção *
                </label>
                <IntentionLetterCandidateField
                  course={courses.find((c) => Number(c.id) === Number(formData.course_id))}
                  value={formData.intentionLetterText}
                  onChange={(text) => setFormData((prev) => ({ ...prev, intentionLetterText: text }))}
                />
              </div>
            </div>
          )}

          {/* Documentos curriculares — análise curricular (não prova/carta; oculto na troca de curso em ordem de inscrição) */}
          {(!selectedProcess || (
            selectedProcess.selection_mode !== 'prova'
            && !isIntentionLetterMode(selectedProcess.selection_mode)
            && !(formData.isCourseChange && isOrdemInscricaoMode(selectedProcess.selection_mode))
          )) && (
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Documentos Curriculares {(!formData.isCourseChange || (formData.isCourseChange && matrixCheckResult?.same_matrix === false)) ? '*' : ''}
                {(!formData.isCourseChange || (formData.isCourseChange && matrixCheckResult?.same_matrix === false)) && (
                  <span style={{ color: '#EF4444', fontSize: '12px', marginLeft: '4px' }}>
                    (Obrigatório para análise curricular)
                  </span>
                )}
              </label>
              <div style={styles.fileUploadArea}>
                <input
                  type="file"
                  multiple
                  onChange={async (e) => {
                    if (!e.target.files?.length) return;
                    const files = Array.from(e.target.files);
                    for (const file of files) {
                      try {
                        const { url, name } = await apiClient.uploadDocument(file, 'curricular');
                        handleCurricularDocumentUpload({
                          name: name || file.name,
                          type: file.type,
                          size: file.size,
                          uploadedAt: new Date().toISOString(),
                          url
                        });
                      } catch (err) {
                        addNotification('Erro', err.message || 'Falha ao enviar documento curricular', 'error');
                      }
                    }
                    e.target.value = '';
                  }}
                  style={{
                    ...styles.fileInput,
                    position: 'relative',
                    width: 'auto',
                    height: 'auto',
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 1,
                    marginBottom: '12px'
                  }}
                  accept={CANDIDATE_DOC_ACCEPT}
                  id="curricular-docs-input"
                />
                
                {formData.isCourseChange && matrixCheckResult?.same_matrix === true ? (
                  <div style={{
                    padding: '20px',
                    border: '2px solid #10B981',
                    borderRadius: '8px',
                    backgroundColor: '#F0FDF4',
                    textAlign: 'center',
                    color: '#065F46',
                    fontSize: '14px'
                  }}>
                    ✅ Documentos curriculares mantidos da inscrição original
                  </div>
                ) : formData.isCourseChange && !matrixCheckResult ? (
                  <div style={{
                    padding: '20px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '8px',
                    backgroundColor: '#F9FAFB',
                    textAlign: 'center',
                    color: '#6B7280',
                    fontSize: '14px'
                  }}>
                    Selecione o novo curso para verificar se será necessário enviar novos documentos
                  </div>
                ) : (
                  <>
                    <label 
                      htmlFor="curricular-docs-input"
                      style={{
                        ...styles.fileUploadText,
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        border: '2px dashed #3B82F6',
                        borderRadius: '8px',
                        backgroundColor: '#F0F9FF',
                        transition: 'all 0.3s',
                        marginBottom: '12px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#E0F2FE';
                        e.target.style.borderColor = '#2563EB';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#F0F9FF';
                        e.target.style.borderColor = '#3B82F6';
                      }}
                    >
                      <span style={{ fontSize: '24px', marginBottom: '8px' }}>📁</span>
                      <span style={{ fontWeight: 'bold', color: '#3B82F6' }}>Clique para selecionar arquivos</span>
                      <span style={{ fontSize: '14px', color: '#6B7280' }}>ou arraste arquivos aqui</span>
                    </label>
                    <p style={styles.fileUploadInfo}>
                      Formatos aceitos: PDF, JPG, PNG (Máx: 5MB cada)
                    </p>
                  </>
                )}
                
                {/* Lista de documentos curriculares enviados */}
                {formData.curricularDocuments.length > 0 && (
                  <div style={styles.documentsList}>
                    <h4 style={{ margin: '8px 0', fontSize: '14px', color: '#374151' }}>
                      Documentos curriculares enviados:
                    </h4>
                    {formData.curricularDocuments.map((doc, index) => (
                      <div key={index} style={styles.documentItem}>
                        <span style={styles.documentName}>{doc.name}</span>
                        <button
                          type="button"
                          onClick={() => handleCurricularDocumentRemove(index)}
                          style={styles.removeDocumentButton}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

              </div>
            </div>
          </div>
          )}

          {formData.isCourseChange && selectedProcess?.has_fee && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#EFF6FF',
              border: '1px solid #3B82F6',
              borderRadius: '8px',
              marginBottom: '16px',
              color: '#1E40AF',
              fontSize: '14px',
              lineHeight: 1.5
            }}>
              ℹ️ <strong>Troca de curso:</strong>{' '}
              {selectedProcess?.selection_mode === 'prova'
                ? 'este passo altera apenas o curso. Documentos curriculares não se aplicam a processos por prova.'
                : isOrdemInscricaoMode(selectedProcess?.selection_mode)
                ? 'este passo altera apenas o curso/unidade. Documentos e pagamento/isenção são mantidos.'
                : 'este passo altera apenas o curso (e os documentos curriculares, se a matriz for diferente).'}{' '}
              Seu pedido de isenção, documentos já enviados para isenção e o status de pagamento <strong>não são alterados</strong> aqui; continuam os mesmos da inscrição atual até análise no fluxo próprio.
            </div>
          )}

          {/* Opção de Isenção de Taxa — oculta na troca de curso; só exibe fora da troca e no período de isenção */}
          {!formData.isCourseChange && selectedProcess?.has_fee && isInExemptionPeriod(selectedProcess) && (
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                {formData.isCourseChange && getPaymentStatus(selectedProcess?.id) === 'paid' ? (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#FEF3C7',
                    border: '1px solid #F59E0B',
                    borderRadius: '6px',
                    color: '#92400E',
                    fontSize: '14px'
                  }}>
                    ℹ️ <strong>Isenção não disponível:</strong> Pagamento já foi confirmado para esta inscrição.
                  </div>
                ) : (
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.requestExemption}
                      onChange={(e) => handleExemptionChange(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Pedir isenção de taxa
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Campos de Isenção — ocultos na troca de curso */}
          {!formData.isCourseChange && formData.requestExemption && selectedProcess?.has_fee && (
            <>

              {selectedProcess?.exemption_cadunico_consulta && (
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>NIS (CadÚnico) *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={14}
                      value={formData.exemptionNis || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, exemptionNis: e.target.value.replace(/\D/g, '').slice(0, 11) }))}
                      placeholder="11 dígitos"
                      style={styles.input}
                    />
                    <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                      Informe o NIS do CadÚnico. O CPF já está no seu cadastro. Documentos abaixo são opcionais.
                    </div>
                  </div>
                </div>
              )}
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Documentos Comprobatórios{selectedProcess?.exemption_cadunico_consulta ? ' (opcional)' : ' *'}
                  </label>
                  <div style={styles.fileUploadArea}>
                    <input
                      type="file"
                      multiple
                      onChange={async (e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          const files = Array.from(e.target.files);
                          for (const file of files) {
                            try {
                              const { url, name } = await apiClient.uploadDocument(file, 'isencao');
                              handleExemptionDocumentUpload({
                                name: name || file.name,
                                type: file.type,
                                size: file.size,
                                uploadedAt: new Date().toISOString(),
                                url
                              });
                            } catch (err) {
                              addNotification('Erro', err.message || 'Falha ao enviar documento', 'error');
                            }
                          }
                          e.target.value = '';
                        }
                      }}
                      style={{
                        ...styles.fileInput,
                        position: 'relative',
                        width: 'auto',
                        height: 'auto',
                        opacity: 0,
                        cursor: 'pointer',
                        zIndex: 1,
                        marginBottom: '12px'
                      }}
                      accept={CANDIDATE_DOC_ACCEPT}
                      id="exemption-docs-input"
                    />
                    <label 
                      htmlFor="exemption-docs-input"
                      style={{
                        ...styles.fileUploadText,
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        border: '2px dashed #F59E0B',
                        borderRadius: '8px',
                        backgroundColor: '#FEF3C7',
                        transition: 'all 0.3s',
                        marginBottom: '12px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#FDE68A';
                        e.target.style.borderColor = '#D97706';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#FEF3C7';
                        e.target.style.borderColor = '#F59E0B';
                      }}
                    >
                      <span style={{ fontSize: '24px', marginBottom: '8px' }}>📄</span>
                      <span style={{ fontWeight: 'bold', color: '#92400E' }}>Clique para selecionar documentos</span>
                      <span style={{ fontSize: '14px', color: '#92400E' }}>ou arraste arquivos aqui</span>
                    </label>
                    <p style={styles.fileUploadInfo}>
                      Formatos aceitos: {CANDIDATE_DOC_FORMATS_LABEL} (máx. 5MB cada)
                    </p>
                  </div>
                  
                  {/* Lista de documentos enviados */}
                  {formData.exemptionDocuments.length > 0 && (
                    <div style={styles.documentsList}>
                      <h4 style={{ margin: '8px 0', fontSize: '14px', color: '#374151' }}>
                        Documentos enviados:
                      </h4>
                      {formData.exemptionDocuments.map((doc, index) => (
                        <div key={index} style={styles.documentItem}>
                          <span style={styles.documentName}>{doc.name}</span>
                          <button
                            type="button"
                            onClick={() => handleExemptionDocumentRemove(index)}
                            style={styles.removeDocumentButton}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {!formData.isCourseChange && selectedProcess && processOffersSpecialAttendance(selectedProcess) && isInSpecialAttendanceRequestPeriod(selectedProcess) && specialAttendanceTypesForModal.length > 0 && (
            <>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    <input
                      type="checkbox"
                      checked={formData.requestSpecialAttendance}
                      onChange={(e) => handleSpecialAttendanceChange(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    Pedir atendimento especial / específico
                  </label>
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#6B7280', lineHeight: 1.5 }}>
                    Marque se precisa de condições especiais na prova (tempo, acessibilidade etc.), conforme os tipos disponíveis neste processo.
                  </p>
                </div>
              </div>
              {formData.requestSpecialAttendance && (
                <>
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <span style={styles.label}>Tipos de atendimento *</span>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                        {specialAttendanceTypesForModal.map((t) => (
                          <label key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={(formData.specialAttendanceTypeIds || []).map(Number).includes(Number(t.id))}
                              onChange={() => toggleSpecialAttendanceType(t.id)}
                              style={{ marginTop: '3px' }}
                            />
                            <span>
                              <strong>{t.name}</strong>
                              {t.requires_attachment ? <span style={{ color: '#B45309' }}> (exige anexo)</span> : null}
                              {t.description ? <span style={{ display: 'block', fontSize: '12px', color: '#6B7280', fontWeight: '400' }}>{t.description}</span> : null}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label} htmlFor="sa-justification">Justificativa / observações</label>
                      <textarea
                        id="sa-justification"
                        value={formData.specialAttendanceJustification}
                        onChange={(e) => handleSpecialAttendanceJustificationChange(e.target.value)}
                        rows={4}
                        style={{ ...styles.input, width: '100%', minHeight: '88px', resize: 'vertical' }}
                        placeholder="Descreva brevemente a necessidade (opcional, mas recomendado)."
                      />
                    </div>
                  </div>
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Documentos comprobatórios</label>
                      <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 8px 0' }}>
                        Envie arquivos quando algum tipo marcado exigir anexo. Formatos: {CANDIDATE_DOC_FORMATS_LABEL} (máx. 5MB cada).
                      </p>
                      <div style={styles.fileUploadArea}>
                        <input
                          type="file"
                          multiple
                          onChange={async (e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              const files = Array.from(e.target.files);
                              for (const file of files) {
                                if (file.size > 5 * 1024 * 1024) {
                                  addNotification('Erro', 'Arquivo muito grande. Máximo 5MB.', 'error');
                                  continue;
                                }
                                try {
                                  const { url, name } = await apiClient.uploadDocument(file, 'special_attendance');
                                  handleSpecialAttendanceDocumentUpload({
                                    name: name || file.name,
                                    type: file.type,
                                    size: file.size,
                                    uploadedAt: new Date().toISOString(),
                                    url
                                  });
                                } catch (err) {
                                  addNotification('Erro', err.message || 'Falha ao enviar documento', 'error');
                                }
                              }
                              e.target.value = '';
                            }
                          }}
                          style={{
                            ...styles.fileInput,
                            position: 'relative',
                            width: 'auto',
                            height: 'auto',
                            opacity: 0,
                            cursor: 'pointer',
                            zIndex: 1,
                            marginBottom: '12px'
                          }}
                          accept={CANDIDATE_DOC_ACCEPT}
                          id="special-attendance-docs-input"
                        />
                        <label
                          htmlFor="special-attendance-docs-input"
                          style={{
                            ...styles.fileUploadText,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px',
                            border: '2px dashed #6366F1',
                            borderRadius: '8px',
                            backgroundColor: '#EEF2FF',
                            transition: 'all 0.3s',
                            marginBottom: '12px'
                          }}
                        >
                          <span style={{ fontSize: '24px', marginBottom: '8px' }}>📎</span>
                          <span style={{ fontWeight: 'bold', color: '#4338CA' }}>Anexar documentos de atendimento especial</span>
                          <span style={{ fontSize: '14px', color: '#4F46E5' }}>Clique para selecionar</span>
                        </label>
                      </div>
                      {formData.specialAttendanceDocuments.length > 0 && (
                        <div style={styles.documentsList}>
                          <h4 style={{ margin: '8px 0', fontSize: '14px', color: '#374151' }}>Documentos enviados:</h4>
                          {formData.specialAttendanceDocuments.map((doc, index) => (
                            <div key={index} style={styles.documentItem}>
                              <span style={styles.documentName}>{doc.name}</span>
                              <button
                                type="button"
                                onClick={() => handleSpecialAttendanceDocumentRemove(index)}
                                style={styles.removeDocumentButton}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {processQuestionnaire && !formData.isCourseChange && unansweredQuestionnaireItems.length > 0 && (
            <p
              style={{
                margin: '0 0 16px 0',
                padding: '10px 12px',
                fontSize: '13px',
                color: '#92400E',
                backgroundColor: '#FFFBEB',
                border: '1px solid #F59E0B',
                borderRadius: '8px',
              }}
            >
              Há <strong>{unansweredQuestionnaireItems.length}</strong> pergunta(s) do questionário socioeconômico sem resposta. Role até a seção <strong>📋 {processQuestionnaire.name}</strong> acima (destaque âmbar) ou use o aviso no topo do questionário.
            </p>
          )}

          <div style={{ 
            padding: '16px', 
            backgroundColor: '#FEF3C7', 
            borderRadius: '8px', 
            marginBottom: '16px' 
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#92400E' }}>📋 Informações Importantes</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#92400E', fontSize: '14px' }}>
              {selectedProcess?.selection_mode !== 'prova' && (
                <li>Verifique se todos os documentos estão em ordem</li>
              )}
              <li>Confirme se o pagamento foi realizado (se aplicável)</li>
              <li>Acompanhe o status da sua inscrição</li>
              <li>Mantenha seus dados atualizados</li>
            </ul>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button
              type="button"
              onClick={() => setShowApplicationModal(false)}
              style={{
                ...styles.button,
                ...styles.buttonSecondary
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Enviando...' : formData.isCourseChange ? 'Confirmar Troca de Curso' : 'Enviar Inscrição'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal para Upload de Novos Documentos */}
      {showDocumentUploadModal && (
        <Modal
          isOpen={showDocumentUploadModal}
          large
          onClose={() => {
            if (actionSubmitting) return;
            setShowDocumentUploadModal(false);
            setSelectedApplicationForUpload(null);
            setNewDocuments([]);
            setDocumentUploadError('');
            setDocumentFileUploading(false);
          }}
          title="📄 Enviar Novo Documento"
        >
          <div style={{ marginBottom: '20px' }}>
            <p style={{ 
              margin: '0 0 16px 0', 
              color: '#6B7280', 
              fontSize: '14px' 
            }}>
              Envie novos documentos para reanálise da sua inscrição.
            </p>
            {documentUploadError && (
              <div
                role="alert"
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#991B1B',
                  fontSize: '14px',
                  lineHeight: 1.45,
                }}
              >
                {documentUploadError}
              </div>
            )}
            
            {/* Motivo de indeferimento */}
            {(selectedApplicationForUpload?.rejection_reason || selectedApplicationForUpload?.rejection_reason_id || selectedApplicationForUpload?.curricular_rejection_reason) && (
              <div style={{
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '16px', marginRight: '8px' }}>❌</span>
                  <span style={{ 
                    fontWeight: '600', 
                    color: '#DC2626',
                    fontSize: '14px'
                  }}>
                    Motivo do Indeferimento:
                  </span>
                </div>
                <p style={{
                  margin: '0',
                  color: '#991B1B',
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}>
                  {(() => {
                    const reason = selectedApplicationForUpload.rejection_reason || selectedApplicationForUpload.rejection_reason_id || selectedApplicationForUpload.curricular_rejection_reason;
                    if (!reason) return 'Motivo não especificado';
                    if (selectedApplicationForUpload.curricular_rejection_reason_title) return selectedApplicationForUpload.curricular_rejection_reason_title;
                    const resolved = /^\d+$/.test(String(reason).trim()) ? getRejectionReasonDescription(reason) : null;
                    return resolved || reason;
                  })()}
                </p>
              </div>
            )}

            {(() => {
              const observation = getCurricularRejectionObservation(selectedApplicationForUpload);
              if (!observation) return null;
              return (
              <div style={{
                backgroundColor: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px', marginRight: '8px' }}>📝</span>
                  <span style={{ fontWeight: '600', color: '#B45309', fontSize: '14px' }}>
                    {observation.kind === 'analyst' ? 'Observações do analista:' : 'Detalhamento do motivo:'}
                  </span>
                </div>
                <p style={{
                  margin: 0,
                  color: '#92400E',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                }}>
                  {observation.text}
                </p>
              </div>
              );
            })()}
          </div>

          <CandidateDocumentFilePicker
            docType="curricular"
            documents={newDocuments}
            onDocumentsChange={setNewDocuments}
            disabled={actionSubmitting}
            onUploadingChange={setDocumentFileUploading}
            label="Documentos Curriculares *"
          />

          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            marginTop: '24px',
            paddingTop: '16px',
            borderTop: '1px solid #E5E7EB'
          }}>
            <button
              type="button"
              disabled={actionSubmitting}
              onClick={() => {
                setShowDocumentUploadModal(false);
                setSelectedApplicationForUpload(null);
                setNewDocuments([]);
                setDocumentUploadError('');
              }}
              style={{
                ...styles.button,
                ...styles.buttonSecondary
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveNewDocuments}
              disabled={actionSubmitting || documentFileUploading || newDocuments.length === 0}
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                opacity: (actionSubmitting || documentFileUploading || newDocuments.length === 0) ? 0.7 : 1
              }}
            >
              {actionSubmitting ? 'Enviando...' : documentFileUploading ? 'Aguardando arquivo…' : 'Enviar Documentos'}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal para Reenviar pedido de isenção (indeferido) */}
      {showResendExemptionModal && selectedApplicationForResendExemption && (
        <Modal
          isOpen={showResendExemptionModal}
          onClose={() => {
            if (actionSubmitting) return;
            setShowResendExemptionModal(false);
            setSelectedApplicationForResendExemption(null);
            setResendExemptionDocs([]);
            setResendExemptionError('');
          }}
          title="📄 Reenviar pedido de isenção"
        >
          <p style={{ margin: '0 0 16px 0', color: '#6B7280', fontSize: '14px' }}>
            Envie novo(s) documento(s) para nova análise. Durante o período de pedido de isenção você pode reenviar ou gerar GRU para pagar.
          </p>
          {resendExemptionError && (
            <div
              role="alert"
              style={{
                marginBottom: '16px',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                color: '#991B1B',
                fontSize: '14px',
              }}
            >
              {resendExemptionError}
            </div>
          )}
          {(() => {
            const proc = (processes || []).find(p => Number(p.id) === Number(selectedApplicationForResendExemption.process_id));
            if (!proc?.exemption_cadunico_consulta) return null;
            return (
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>NIS (CadÚnico) *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={11}
                  value={resendExemptionNis}
                  onChange={(e) => setResendExemptionNis(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  placeholder="11 dígitos"
                  style={styles.input}
                />
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                  Documentos abaixo são opcionais se o NIS estiver preenchido.
                </div>
              </div>
            );
          })()}
          {selectedApplicationForResendExemption.exemption_reason && (
            <div style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <strong style={{ color: '#DC2626' }}>Motivo do indeferimento:</strong>
              <p style={{ margin: '8px 0 0 0', color: '#991B1B', fontSize: '14px' }}>
                {/^\d+$/.test(String(selectedApplicationForResendExemption.exemption_reason).trim())
                  ? getRejectionReasonDescription(selectedApplicationForResendExemption.exemption_reason) || selectedApplicationForResendExemption.exemption_reason
                  : selectedApplicationForResendExemption.exemption_reason}
              </p>
            </div>
          )}
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
            Documentos de isenção{(processes || []).find(p => Number(p.id) === Number(selectedApplicationForResendExemption.process_id))?.exemption_cadunico_consulta ? ' (opcional)' : ' *'}
          </label>
          <input
            type="file"
            multiple
            accept={CANDIDATE_DOC_ACCEPT}
            onChange={async (e) => {
              if (!e.target.files?.length) return;
              for (const file of Array.from(e.target.files)) {
                try {
                  const { url, name } = await apiClient.uploadDocument(file, 'isencao');
                  setResendExemptionDocs(prev => [...prev, { name: name || file.name, type: file.type, size: file.size, url }]);
                } catch (err) {
                  addNotification('Erro', err.message || 'Falha ao enviar documento', 'error');
                }
              }
              e.target.value = '';
            }}
            style={{ marginBottom: '12px' }}
          />
          {resendExemptionDocs.length > 0 && (
            <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px' }}>
              {resendExemptionDocs.map((d, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span>{d.name}</span>
                  <button type="button" onClick={() => setResendExemptionDocs(prev => prev.filter((_, j) => j !== i))} style={{ fontSize: '12px', color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer' }}>Remover</button>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button type="button" disabled={actionSubmitting} onClick={() => { setShowResendExemptionModal(false); setSelectedApplicationForResendExemption(null); setResendExemptionDocs([]); setResendExemptionError(''); }} style={{ ...styles.button, ...styles.buttonSecondary, opacity: actionSubmitting ? 0.7 : 1 }}>Cancelar</button>
            <button type="button" onClick={handleSaveResendExemption} disabled={actionSubmitting || (resendExemptionDocs.length === 0 && String(resendExemptionNis || '').replace(/\D/g, '').length !== 11)} style={{ ...styles.button, ...styles.buttonPrimary, opacity: (actionSubmitting || (resendExemptionDocs.length === 0 && String(resendExemptionNis || '').replace(/\D/g, '').length !== 11)) ? 0.7 : 1 }}>{actionSubmitting ? 'Enviando...' : 'Reenviar pedido'}</button>
          </div>
        </Modal>
      )}

      {/* Modal reenvio atendimento especial (indeferido, no período de pedido) */}
      {showResendSpecialAttendanceModal && selectedApplicationForResendSpecialAttendance && (
        <Modal
          isOpen={showResendSpecialAttendanceModal}
          onClose={() => {
            if (actionSubmitting) return;
            setShowResendSpecialAttendanceModal(false);
            setSelectedApplicationForResendSpecialAttendance(null);
            setResendSpecialAttendanceDocs([]);
            setResendSpecialAttendanceError('');
          }}
          title="📄 Reenviar atendimento especial"
        >
          <p style={{ margin: '0 0 16px 0', color: '#6B7280', fontSize: '14px' }}>
            Envie novo(s) documento(s) para nova análise. Válido durante o período de pedido de atendimento especial.
          </p>
          {resendSpecialAttendanceError && (
            <div
              role="alert"
              style={{
                marginBottom: '16px',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                color: '#991B1B',
                fontSize: '14px',
              }}
            >
              {resendSpecialAttendanceError}
            </div>
          )}
          {selectedApplicationForResendSpecialAttendance.special_attendance_rejection_reason && (
            <div style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
              <strong style={{ color: '#DC2626' }}>Motivo do indeferimento:</strong>
              <p style={{ margin: '8px 0 0 0', color: '#991B1B', fontSize: '14px' }}>
                {/^\d+$/.test(String(selectedApplicationForResendSpecialAttendance.special_attendance_rejection_reason).trim())
                  ? getRejectionReasonDescription(selectedApplicationForResendSpecialAttendance.special_attendance_rejection_reason) || selectedApplicationForResendSpecialAttendance.special_attendance_rejection_reason
                  : selectedApplicationForResendSpecialAttendance.special_attendance_rejection_reason}
              </p>
            </div>
          )}
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>Documentos *</label>
          <input
            type="file"
            multiple
            accept={CANDIDATE_DOC_ACCEPT}
            onChange={async (e) => {
              if (!e.target.files?.length) return;
              for (const file of Array.from(e.target.files)) {
                try {
                  const { url, name } = await apiClient.uploadDocument(file, 'special_attendance');
                  setResendSpecialAttendanceDocs((prev) => [...prev, { name: name || file.name, type: file.type, size: file.size, url }]);
                } catch (err) {
                  addNotification('Erro', err.message || 'Falha ao enviar documento', 'error');
                }
              }
              e.target.value = '';
            }}
            style={{ marginBottom: '12px' }}
          />
          {resendSpecialAttendanceDocs.length > 0 && (
            <ul style={{ margin: '0 0 16px 0', paddingLeft: '20px' }}>
              {resendSpecialAttendanceDocs.map((d, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span>{d.name}</span>
                  <button type="button" onClick={() => setResendSpecialAttendanceDocs((prev) => prev.filter((_, j) => j !== i))} style={{ fontSize: '12px', color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer' }}>Remover</button>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button type="button" disabled={actionSubmitting} onClick={() => { setShowResendSpecialAttendanceModal(false); setSelectedApplicationForResendSpecialAttendance(null); setResendSpecialAttendanceDocs([]); setResendSpecialAttendanceError(''); }} style={{ ...styles.button, ...styles.buttonSecondary, opacity: actionSubmitting ? 0.7 : 1 }}>Cancelar</button>
            <button type="button" onClick={handleSaveResendSpecialAttendance} disabled={actionSubmitting || resendSpecialAttendanceDocs.length === 0} style={{ ...styles.button, ...styles.buttonPrimary, opacity: (actionSubmitting || resendSpecialAttendanceDocs.length === 0) ? 0.7 : 1 }}>{actionSubmitting ? 'Enviando...' : 'Reenviar pedido'}</button>
          </div>
        </Modal>
      )}

      {/* Modal Alterar cota */}
      {showQuotaChangeModal && selectedApplicationForQuotaChange && (() => {
        const app = selectedApplicationForQuotaChange;
        const proc = processes.find(p => p.id === app.process_id);
        const crs = courses.find(c => c.id === app.course_id);
        const qv = crs?.quota_vacancies || [];
        const useQuotas = proc?.use_quotas === true;
        return (
          <Modal
            isOpen={showQuotaChangeModal}
            onRequestClose={() => {
              setShowQuotaChangeModal(false);
              setSelectedApplicationForQuotaChange(null);
              setQuotaChangeSelectedQuota('');
            }}
            style={{
              overlay: { backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
              content: { position: 'relative', width: '90%', maxWidth: '500px', backgroundColor: 'white', borderRadius: '12px', padding: '24px', border: 'none', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }
            }}
          >
            <h2 style={{ margin: '0 0 8px 0', color: '#1F2937', fontSize: '20px', fontWeight: '600' }}>
              📋 Alterar cota
            </h2>
            <p style={{ margin: '0 0 16px 0', color: '#6B7280', fontSize: '14px' }}>
              {crs?.name || 'Curso'} — {proc?.name || 'Processo'}
            </p>
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#92400E'
            }}>
              ⚠️ <strong>Veja se esta é a cota desejada e que de fato você se encaixa nela,</strong> pois terá que passar por etapa de comprovação em etapa específica deste processo seletivo.
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Nova cota</label>
              <select
                value={quotaChangeSelectedQuota}
                onChange={(e) => setQuotaChangeSelectedQuota(e.target.value)}
                style={styles.input}
              >
                {useQuotas && qv.map((item) => {
                  const quota = quotas.find(q => q.id === item.quota_id);
                  const code = quota?.code || 'AC';
                  const desc = quota?.description || 'Ampla Concorrência';
                  return (
                    <option key={item.quota_id} value={code}>
                      {code} - {desc}
                    </option>
                  );
                })}
                {(!useQuotas || qv.length === 0) && (
                  <option value="AC">AC - Ampla Concorrência</option>
                )}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" onClick={() => { setShowQuotaChangeModal(false); setSelectedApplicationForQuotaChange(null); setQuotaChangeSelectedQuota(''); }} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancelar</button>
              <button type="button" onClick={handleConfirmQuotaChange} disabled={loading || quotaChangeSelectedQuota === (app.quota_type || 'AC')} style={{ ...styles.button, ...styles.buttonPrimary, opacity: (loading || quotaChangeSelectedQuota === (app.quota_type || 'AC')) ? 0.7 : 1 }}>{loading ? 'Salvando...' : 'Confirmar'}</button>
            </div>
          </Modal>
        );
      })()}

      {/* Modal para Recurso */}
      {showAppealModal && (
        <Modal
          isOpen={showAppealModal}
          large
          onClose={() => {
            if (actionSubmitting) return;
            setShowAppealModal(false);
            setSelectedApplicationForAppeal(null);
            setAppealForm({ reason: '', documents: [], justification: '', question_number: '' });
            setAppealFormErrors({});
            setAppealSubmitError('');
          }}
          title="📝 Solicitar Recurso"
        >
          <div style={{ marginBottom: '20px' }}>
            <p style={{ 
              margin: '0 0 16px 0', 
              color: '#6B7280', 
              fontSize: '14px' 
            }}>
              {appealType === 'list'
                ? 'Solicite recurso contra a Lista Preliminar de inscrições Deferidas e Indeferidas.'
                : appealType === 'preliminary_result'
                ? 'Solicite recurso contra o Resultado preliminar (classificação, precedência de vagas, remanejamento, média ponderada, etc.).'
                : appealType === 'special_attendance'
                ? 'Solicite recurso contra o indeferimento do seu pedido de atendimento especial (condições de prova, tempo adicional, etc.).'
                : appealType === 'answer_key'
                ? 'No período de recurso contra o gabarito preliminar, escolha a questão e descreva argumentação e pedido por escrito (sem anexos).'
                : 'Solicite recurso contra o indeferimento da sua isenção.'}
            </p>
            {appealSubmitError && (
              <div
                role="alert"
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#991B1B',
                  fontSize: '14px',
                }}
              >
                {appealSubmitError}
              </div>
            )}
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#92400E'
            }}>
              {appealType === 'answer_key' ? (
                <>
                  ⚠️ <strong>Um recurso por questão.</strong> Você pode recorrer de várias questões, mas não pode repetir a mesma.
                  {selectedApplicationForAppeal && (() => {
                    const appealed = getAnswerKeyAppealedQuestionNumbers(selectedApplicationForAppeal);
                    if (appealed.size === 0) return null;
                    return (
                      <span style={{ display: 'block', marginTop: '8px' }}>
                        Já recorridas: {[...appealed].sort((a, b) => a - b).join(', ')}
                      </span>
                    );
                  })()}
                </>
              ) : (
                <>⚠️ <strong>Você tem apenas uma chance</strong> de enviar este recurso. Verifique os campos antes de enviar.</>
              )}
            </div>
            
            {/* Informações da aplicação */}
            {selectedApplicationForAppeal && (
              <div style={{
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontSize: '16px', marginRight: '8px' }}>📋</span>
                  <span style={{ 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '14px'
                  }}>
                    Informações da Inscrição:
                  </span>
                </div>
                <p style={{
                  margin: '0',
                  color: '#6B7280',
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}>
                  Processo: {processes.find(p => p.id === selectedApplicationForAppeal.process_id)?.name || 'N/A'}<br/>
                  Curso: {formatCourseDisplayFromObject(courses.find(c => c.id === selectedApplicationForAppeal.course_id))}
                  {appealType === 'exemption' && selectedApplicationForAppeal.exemption_reason && (
                    <><br/>Motivo do Indeferimento: {selectedApplicationForAppeal.exemption_reason || 'Não especificado'}</>
                  )}
                  {appealType === 'special_attendance' && selectedApplicationForAppeal.special_attendance_rejection_reason && (
                    <><br/>Motivo do Indeferimento (atendimento especial): {/^\d+$/.test(String(selectedApplicationForAppeal.special_attendance_rejection_reason).trim())
                      ? getRejectionReasonDescription(selectedApplicationForAppeal.special_attendance_rejection_reason) || selectedApplicationForAppeal.special_attendance_rejection_reason
                      : selectedApplicationForAppeal.special_attendance_rejection_reason}</>
                  )}
                  {appealType === 'list' && selectedApplicationForAppeal.analysis_status === 'rejected' && (() => {
                    const app = selectedApplicationForAppeal;
                    const reasonTitle = app.curricular_rejection_reason_title;
                    const reason = (app.rejection_reason || app.curricular_rejection_reason || '').toString().trim();
                    let motivo = null;
                    if (reasonTitle) motivo = reasonTitle;
                    else if (reason) {
                      motivo = /^\d+$/.test(reason)
                        ? (getRejectionReasonDescription(reason) || reason)
                        : reason;
                    }
                    const obs = getCurricularRejectionObservation(app);
                    if (!motivo && !obs?.text) return null;
                    return (
                      <>
                        {motivo && <><br/>Motivo do indeferimento (lista preliminar): {motivo}</>}
                        {obs?.text && <><br/>Observação: {obs.text}</>}
                      </>
                    );
                  })()}
                </p>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSaveAppeal(); }}>
            {Object.keys(appealFormErrors).length > 0 && (
              <Alert
                message="Preencha todos os campos obrigatórios antes de enviar."
                type="warning"
                onClose={() => setAppealFormErrors({})}
              />
            )}
            {appealType === 'answer_key' && selectedApplicationForAppeal && (() => {
              const proc = processes.find((p) => p.id === selectedApplicationForAppeal.process_id);
              const total = totalExamQuestionsForProcess(proc);
              const appealed = getAnswerKeyAppealedQuestionNumbers(selectedApplicationForAppeal);
              const options = [];
              for (let q = 1; q <= total; q += 1) {
                if (!appealed.has(q)) options.push(q);
              }
              return (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: appealFormErrors.question_number ? '#DC2626' : '#374151'
                  }}>
                    Questão *
                  </label>
                  <select
                    value={appealForm.question_number}
                    onChange={(e) => {
                      setAppealForm((prev) => ({ ...prev, question_number: e.target.value }));
                      if (appealFormErrors.question_number) {
                        setAppealFormErrors((prev) => {
                          const next = { ...prev };
                          delete next.question_number;
                          return next;
                        });
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: appealFormErrors.question_number ? '2px solid #DC2626' : '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Selecione a questão</option>
                    {options.map((q) => (
                      <option key={q} value={String(q)}>Questão {q}</option>
                    ))}
                  </select>
                  {appealFormErrors.question_number && (
                    <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px', display: 'block' }}>
                      {appealFormErrors.question_number}
                    </span>
                  )}
                  {appealForm.question_number && (
                    <div
                      style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: '#EFF6FF',
                        border: '1px solid #BFDBFE',
                        borderRadius: '8px',
                        fontSize: '13px',
                        color: '#1E3A8A',
                        lineHeight: 1.5,
                      }}
                    >
                      {answerKeyHintLoading && 'Carregando resposta do gabarito preliminar…'}
                      {!answerKeyHintLoading && answerKeyHintError && (
                        <span style={{ color: '#B45309' }}>{answerKeyHintError}</span>
                      )}
                      {!answerKeyHintLoading && !answerKeyHintError && answerKeyQuestionHint && (
                        <>
                          <strong>Gabarito preliminar — questão {answerKeyQuestionHint.question_number}</strong>
                          <div style={{ marginTop: '6px' }}>
                            Tipo de folha: <strong>{answerKeyQuestionHint.variant_label}</strong>
                            {answerKeyQuestionHint.variant_uncertain && (
                              <span style={{ color: '#6B7280' }}>
                                {' '}
                                (confira o tipo impresso na sua folha de respostas)
                              </span>
                            )}
                          </div>
                          <div style={{ marginTop: '6px' }}>
                            Resposta oficial: <strong>{answerKeyQuestionHint.answer_display}</strong>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            {appealType !== 'answer_key' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: appealFormErrors.reason ? '#DC2626' : '#374151'
              }}>
                Motivo do Recurso *
              </label>
              <input
                type="text"
                value={appealForm.reason}
                onChange={(e) => {
                  setAppealForm(prev => ({ ...prev, reason: e.target.value }));
                  if (appealFormErrors.reason) setAppealFormErrors(prev => { const next = { ...prev }; delete next.reason; return next; });
                }}
                placeholder="Digite o motivo do seu recurso (ex.: Erro na documentação apresentada)"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: appealFormErrors.reason ? '2px solid #DC2626' : '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              />
              {appealFormErrors.reason && (
                <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px', display: 'block' }}>
                  {appealFormErrors.reason}
                </span>
              )}
            </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: appealFormErrors.justification ? '#DC2626' : '#374151'
              }}>
                {appealType === 'answer_key' ? 'Argumentação e pedido *' : 'Justificativa Detalhada *'}
              </label>
              <textarea
                value={appealForm.justification}
                onChange={(e) => {
                  setAppealForm(prev => ({ ...prev, justification: e.target.value }));
                  if (appealFormErrors.justification) setAppealFormErrors(prev => { const next = { ...prev }; delete next.justification; return next; });
                }}
                placeholder={appealType === 'answer_key'
                  ? 'Explique por que a resposta do gabarito deveria ser alterada e qual correção você solicita...'
                  : 'Descreva detalhadamente o motivo do seu recurso...'}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: appealFormErrors.justification ? '2px solid #DC2626' : '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  minHeight: appealType === 'answer_key' ? '140px' : '100px',
                  resize: 'vertical'
                }}
              />
              {appealFormErrors.justification && (
                <span style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px', display: 'block' }}>
                  {appealFormErrors.justification}
                </span>
              )}
            </div>

            {appealType !== 'answer_key' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Documentos Comprobatórios (Opcional)
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="file"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    const validFiles = files.filter((file) => {
                      if (!isAllowedCandidateDocumentFile(file)) {
                        addNotification('Aviso', candidateDocumentRejectReason(file), 'warning');
                        return false;
                      }
                      return true;
                    });
                    const newDocs = [];
                    for (const file of validFiles) {
                      try {
                        const { url, name } = await apiClient.uploadDocument(file, 'recurso');
                        newDocs.push({ url, name, type: file.type, size: file.size, uploadedAt: new Date().toISOString() });
                      } catch (err) {
                        addNotification('Erro', err.message || 'Falha ao enviar documento', 'error');
                      }
                    }
                    if (newDocs.length > 0) {
                      setAppealForm(prev => ({ ...prev, documents: [...prev.documents, ...newDocs] }));
                    }
                    e.target.value = '';
                  }}
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    zIndex: 1
                  }}
                  accept={CANDIDATE_DOC_ACCEPT}
                  id="appeal-docs-input"
                />
                <label 
                  htmlFor="appeal-docs-input"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    border: '2px dashed #8B5CF6',
                    borderRadius: '8px',
                    backgroundColor: '#F3E8FF',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#EDE9FE';
                    e.target.style.borderColor = '#7C3AED';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#F3E8FF';
                    e.target.style.borderColor = '#8B5CF6';
                  }}
                >
                  <span style={{ fontSize: '24px', marginBottom: '8px' }}>📄</span>
                  <span style={{ fontWeight: 'bold', color: '#7C3AED' }}>Clique para selecionar documentos</span>
                  <span style={{ fontSize: '14px', color: '#7C3AED' }}>ou arraste arquivos aqui</span>
                </label>
              </div>
              
              {appealForm.documents.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
                    Documentos selecionados:
                  </p>
                  {appealForm.documents.map((doc, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: '#F3F4F6',
                      borderRadius: '6px',
                      marginBottom: '4px'
                    }}>
                      <span style={{ fontSize: '14px', color: '#374151' }}>
                        📄 {doc.name} ({(doc.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                      <button
                        type="button"
                        onClick={() => setAppealForm(prev => ({ 
                          ...prev, 
                          documents: prev.documents.filter((_, i) => i !== index) 
                        }))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#DC2626',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: '4px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                Formatos aceitos: {CANDIDATE_DOC_FORMATS_LABEL} (máx. 5MB cada)
              </p>
            </div>
            )}

            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #E5E7EB'
            }}>
              <button
                type="button"
                disabled={actionSubmitting}
                onClick={() => {
                  if (actionSubmitting) return;
                  setShowAppealModal(false);
                  setSelectedApplicationForAppeal(null);
                  setAppealForm({ reason: '', documents: [], justification: '', question_number: '' });
                  setAppealFormErrors({});
                  setAppealSubmitError('');
                }}
                style={{
                  ...styles.button,
                  ...styles.buttonSecondary,
                  opacity: actionSubmitting ? 0.7 : 1,
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={actionSubmitting}
                  style={{
                    ...styles.button,
                    ...styles.buttonDanger,
                    opacity: actionSubmitting ? 0.7 : 1
                  }}
              >
                {actionSubmitting ? 'Enviando...' : 'Enviar Recurso'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal Ver Recurso - leitura, salvar, imprimir (isenção, lista ou resultado preliminar) - com abas quando múltiplos recursos */}
      {showAppealViewModal && selectedApplicationForAppealView && (() => {
        const allAppeals = buildAppealsListForView(selectedApplicationForAppealView);
        return allAppeals.length > 0;
      })() && (() => {
        const app = selectedApplicationForAppealView;
        const allAppeals = buildAppealsListForView(app);
        const appealLabels = {
          exemption: 'Recurso contra o resultado do pedido de isenção',
          list: 'Recurso contra a Lista Preliminar de inscrições',
          preliminary_result: 'Recurso contra o Resultado preliminar',
          special_attendance: 'Recurso contra indeferimento do atendimento especial',
          answer_key: 'Recurso contra o gabarito',
        };
        const appealTabLabel = (a) => {
          if (a.appeal_type === 'answer_key' && a.question_number) {
            return `Gabarito — questão ${a.question_number}`;
          }
          return appealLabels[a.appeal_type] || 'Recurso';
        };
        const activeAppeal = allAppeals[appealViewTabIndex] || allAppeals[0];
        return (
          <Modal
            isOpen={showAppealViewModal}
            onClose={() => { setShowAppealViewModal(false); setSelectedApplicationForAppealView(null); }}
            title={`📋 Meus Recursos${allAppeals.length > 1 ? ` (${allAppeals.length})` : ''}`}
          >
            {allAppeals.length > 1 && (
              <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', flexWrap: 'wrap', borderBottom: '1px solid #E5E7EB', paddingBottom: '12px' }}>
                {allAppeals.map((a, idx) => (
                  <button
                    key={idx}
                    onClick={() => setAppealViewTabIndex(idx)}
                    style={{
                      padding: '8px 14px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      background: appealViewTabIndex === idx ? '#EFF6FF' : '#F9FAFB',
                      color: appealViewTabIndex === idx ? '#1D4ED8' : '#374151',
                      fontWeight: appealViewTabIndex === idx ? 600 : 400,
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {appealTabLabel(a)}
                  </button>
                ))}
              </div>
            )}
            <div id="appeal-print-content">
              {activeAppeal && (() => {
                const appealData = activeAppeal;
                const proc = processes.find(p => p.id === app.process_id);
                const course = courses.find(c => c.id === app.course_id);
                const statusLabel = appealData.status === 'pending' ? 'Aguardando análise' : (appealData.status === 'approved' || appealData.status === 'appeal_approved') ? 'Aprovado' : (appealData.status === 'rejected' || appealData.status === 'appeal_rejected') ? 'Indeferido' : appealData.status;
                const appealTypeLabel = getAppealTypeLabel(appealData.appeal_type) || appealLabels[appealData.appeal_type] || 'Meu Recurso';
                return (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Tipo de recurso</div>
                      <div style={{ color: '#6B7280' }}>{appealTypeLabel}</div>
                    </div>
                    {appealData.appeal_type === 'answer_key' && appealData.question_number && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Questão</div>
                        <div style={{ color: '#6B7280' }}>{appealData.question_number}</div>
                      </div>
                    )}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Processo</div>
                      <div style={{ color: '#6B7280' }}>{proc?.name || '-'}</div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Curso</div>
                      <div style={{ color: '#6B7280' }}>{formatCourseDisplayFromObject(course)}</div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Data do envio</div>
                      <div style={{ color: '#6B7280' }}>{appealData.created_at ? formatDate(appealData.created_at) : '-'}</div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Status</div>
                      <div style={{ color: '#6B7280' }}>{statusLabel}</div>
                    </div>
                    {appealData.candidate_official_response_visible && (
                      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#ECFDF5', borderRadius: '8px', border: '1px solid #A7F3D0' }}>
                        <div style={{ fontWeight: '600', color: '#047857', marginBottom: '8px' }}>Resposta oficial ao recurso</div>
                        <div style={{ color: '#374151' }}>
                          <strong>Decisão:</strong>{' '}
                          {appealData.official_decision === 'approved' ? 'Deferido' : appealData.official_decision === 'rejected' ? 'Indeferido' : '—'}
                        </div>
                        <div style={{ marginTop: '10px', fontWeight: '600', color: '#374151' }}>Fundamentação</div>
                        <div style={{ color: '#374151', whiteSpace: 'pre-wrap', marginTop: '4px' }}>
                          {appealData.official_fundamentacao || '(não informada)'}
                        </div>
                      </div>
                    )}
                    {appealData.process_allows_candidate_appeal_response &&
                      !appealData.candidate_official_response_visible &&
                      ['approved', 'rejected'].includes(String(appealData.status || '').toLowerCase()) && (
                      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '8px', border: '1px solid #FCD34D', fontSize: '14px', color: '#92400E' }}>
                        Sua solicitação já foi analisada. A exibição da resposta oficial nesta tela depende da publicação pelo administrador; acompanhe também o edital e os canais oficiais.
                      </div>
                    )}
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                        {appealData.appeal_type === 'answer_key' ? 'Argumentação e pedido' : 'Justificativa'}
                      </div>
                      <div style={{ color: '#374151', whiteSpace: 'pre-wrap', backgroundColor: '#F9FAFB', padding: '12px', borderRadius: '8px' }}>
                        {appealData.justification || '(não informada)'}
                      </div>
                    </div>
                    {appealData.appeal_type !== 'answer_key' && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Documentos anexados</div>
                      <ul style={{ margin: 0, paddingLeft: '20px', color: '#6B7280' }}>
                        {(appealData.documents || []).length === 0 ? (
                          <li>(nenhum)</li>
                        ) : (
                          (appealData.documents || []).map((d, i) => (
                            <li key={i}>{d.name || d.url || `Documento ${i + 1}`}</li>
                          ))
                        )}
                      </ul>
                    </div>
                    )}
                  </>
                );
              })()}
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
              <button
                onClick={handlePrintAppeal}
                style={{ ...styles.button, ...styles.buttonPrimary }}
              >
                🖨️ Imprimir
              </button>
              <button
                onClick={() => handleSaveAppealToFile(appealViewTabIndex)}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                💾 Salvar
              </button>
              <button
                onClick={() => { setShowAppealViewModal(false); setSelectedApplicationForAppealView(null); }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Fechar
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* Modal Meus dados cadastrais */}
      {(showProfileModal || profileModalOpen) && profileFormData && (
        <Modal
          isOpen={showProfileModal || profileModalOpen}
          onClose={() => { setProfileModalOpen(false); setProfileFormData(null); setProfileError(''); onCloseProfileModal?.(); }}
          title="Meus dados cadastrais"
        >
          <form onSubmit={handleProfileSubmit} style={styles.form}>
            {profileError && <Alert message={profileError} type="error" onClose={() => setProfileError('')} />}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={styles.label}>Nome *</label>
                <input type="text" value={profileFormData.name} onChange={(e) => handleProfileInputChange('name', e.target.value)} style={styles.input} required />
              </div>
              <div>
                <label style={styles.label}>Nome da Mãe *</label>
                <input type="text" value={profileFormData.mother_name} onChange={(e) => handleProfileInputChange('mother_name', e.target.value)} style={styles.input} required />
              </div>
              <div>
                <label style={styles.label}>Nome do Pai</label>
                <input type="text" value={profileFormData.father_name} onChange={(e) => handleProfileInputChange('father_name', e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Data de Nascimento *</label>
                <input type="date" value={profileFormData.birth_date} onChange={(e) => handleProfileInputChange('birth_date', e.target.value)} style={styles.input} required />
              </div>
              <div>
                <label style={styles.label}>CPF *</label>
                <input type="text" value={profileFormData.cpf} onChange={(e) => handleProfileInputChange('cpf', formatCPF(e.target.value))} style={styles.input} maxLength={14} required />
              </div>
              <div>
                <label style={styles.label}>Documento</label>
                <select value={profileFormData.identity_type} onChange={(e) => handleProfileInputChange('identity_type', e.target.value)} style={styles.select}>
                  <option value="CI">CI</option>
                  <option value="CNH">CNH</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Número do Documento *</label>
                <input type="text" value={profileFormData.identity_number} onChange={(e) => handleProfileInputChange('identity_number', e.target.value)} style={styles.input} required />
              </div>
              <div>
                <label style={styles.label}>Órgão Emissor *</label>
                <input type="text" value={profileFormData.issuing_org} onChange={(e) => handleProfileInputChange('issuing_org', e.target.value)} style={styles.input} required />
              </div>
              <div>
                <label style={styles.label}>Data de Emissão *</label>
                <input type="date" value={profileFormData.issuing_date} onChange={(e) => handleProfileInputChange('issuing_date', e.target.value)} style={styles.input} required />
              </div>
              <div>
                <label style={styles.label}>Telefone *</label>
                <input type="text" value={profileFormData.phone} onChange={(e) => handleProfileInputChange('phone', formatPhone(e.target.value))} style={styles.input} required />
              </div>
              <div>
                <label style={styles.label}>Nome Social</label>
                <input type="text" value={profileFormData.social_name} onChange={(e) => handleProfileInputChange('social_name', e.target.value)} style={styles.input} placeholder="Opcional" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input type="checkbox" id="profile_use_social" checked={profileFormData.use_social_name} onChange={(e) => handleProfileInputChange('use_social_name', e.target.checked)} style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                <label htmlFor="profile_use_social" style={{ fontSize: '14px' }}>Usar nome social em todas as publicações</label>
              </div>
            </div>
            <div style={{ marginTop: '16px' }}>
              <label style={styles.label}>Endereço - Rua *</label>
              <input type="text" value={profileFormData.address?.street || ''} onChange={(e) => handleProfileInputChange('address.street', e.target.value)} style={styles.input} required />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
              <div>
                <label style={styles.label}>Número</label>
                <input type="text" value={profileFormData.address?.number || ''} onChange={(e) => handleProfileInputChange('address.number', e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Complemento</label>
                <input type="text" value={profileFormData.address?.complement || ''} onChange={(e) => handleProfileInputChange('address.complement', e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Bairro</label>
                <input type="text" value={profileFormData.address?.neighborhood || ''} onChange={(e) => handleProfileInputChange('address.neighborhood', e.target.value)} style={styles.input} />
              </div>
              <div>
                <label style={styles.label}>Cidade *</label>
                <input type="text" value={profileFormData.address?.city || ''} onChange={(e) => handleProfileInputChange('address.city', e.target.value)} style={styles.input} required />
              </div>
              <div>
                <label style={styles.label}>Estado *</label>
                <input type="text" value={profileFormData.address?.state || ''} onChange={(e) => handleProfileInputChange('address.state', e.target.value)} style={styles.input} placeholder="UF" maxLength={2} required />
              </div>
              <div>
                <label style={styles.label}>CEP</label>
                <input type="text" value={profileFormData.address?.zipCode || ''} onChange={(e) => handleProfileInputChange('address.zipCode', e.target.value)} style={styles.input} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button type="submit" disabled={profileSaving} style={{ ...styles.button, ...styles.buttonPrimary }}>
                {profileSaving ? 'Salvando...' : 'Salvar'}
              </button>
              <button type="button" onClick={() => { setProfileModalOpen(false); setProfileFormData(null); onCloseProfileModal?.(); }} style={{ ...styles.button, ...styles.buttonSecondary }}>
                Cancelar
              </button>
            </div>
          </form>
        </Modal>
      )}

      <Modal
        isOpen={!!docsModalProcess}
        onClose={() => setDocsModalProcess(null)}
        title={docsModalProcess ? `Editais e anexos - ${docsModalProcess.name}` : ''}
      >
        {docsModalProcess && (
          <>
            {loadingDocs ? (
              <LoadingSpinner />
            ) : processDocuments.length === 0 ? (
              <p style={{ color: '#6B7280', textAlign: 'center', padding: '24px' }}>Nenhum documento disponível.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Nome</th>
                    <th style={styles.tableHeader}>Publicação</th>
                    <th style={styles.tableHeader}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {processDocuments.map((doc) => (
                    <tr key={doc.id}>
                      <td style={styles.tableCell}>{doc.name}</td>
                      <td style={styles.tableCell}>{doc.published_at ? formatDate(doc.published_at) : '-'}</td>
                      <td style={styles.tableCell}>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const blob = await apiClient.downloadProcessDocument(doc.id);
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = doc.file_name || doc.name || 'documento';
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch (err) {
                              addNotification('Erro', err.message || 'Falha ao baixar', 'error');
                            }
                          }}
                          style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                        >
                          ⬇️ Baixar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Modal>

      <Modal
        isOpen={!!scheduleModalProcess}
        onClose={() => setScheduleModalProcess(null)}
        title={scheduleModalProcess ? `Cronograma - ${scheduleModalProcess.name}` : ''}
      >
        {scheduleModalProcess && (
          <>
            {loadingSchedule ? (
              <LoadingSpinner />
            ) : scheduleItems.length === 0 ? (
              <p style={{ color: '#6B7280', textAlign: 'center', padding: '24px' }}>Nenhum item no cronograma.</p>
            ) : (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>Etapa</th>
                    <th style={styles.tableHeader}>Data(s)</th>
                    <th style={styles.tableHeader}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleItems.map((item) => {
                    const datesStr = item.date_type === 'single'
                      ? (item.single_date ? formatDate(item.single_date) : '-')
                      : [item.start_date, item.end_date].filter(Boolean).map(formatDate).join(' a ') || '-';
                    return (
                      <tr key={item.source === 'manual' ? `m-${item.id}` : item.id}>
                        <td style={styles.tableCell}>{item.label}</td>
                        <td style={styles.tableCell}>{datesStr}</td>
                        <td style={styles.tableCell}>
                          <span style={{
                            ...styles.statusBadge,
                            fontSize: '12px',
                            padding: '2px 8px',
                            ...(item.status === 'aberto' ? styles.statusApproved : item.status === 'nao_iniciado' ? { backgroundColor: '#FEF3C7', color: '#92400E' } : { backgroundColor: '#F3F4F6', color: '#6B7280' })
                          }}>
                            {item.status === 'aberto' ? 'Aberto' : item.status === 'nao_iniciado' ? 'Não iniciado' : 'Encerrado'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}); 
